{
  "app": {
    "name": "Core Hub"
  },
  "nav": {
    "new_chat": "New chat",
    "search": "Search",
    "device_connections": "Device connections",
    "agent_manager": "Agents",
    "models": "Models",
    "knowledge": "Knowledge",
    "chat": "Chat",
    "rooms": "Rooms",
    "tasks": "Tasks",
    "schedules": "Schedules",
    "settings": "Settings",
    "account": "Account",
    "users": "Users",
    "webhooks": "Webhooks",
    "display": "Display",
    "notifications": "Notifications",
    "privacy": "Privacy",
    "this_device": "This device",
    "about": "About",
    "logs": "Logs",
    "usage": "Usage",
    "skills_usage": "Skills usage",
    "performance": "Performance",
    "theme": "Theme",
    "workspaces": "Profiles",
    "updates": "Updates",
    "plugins": "Plugins",
    "skills": "Skills",
    "mcp": "MCP",
    "memory": "Memory",
    "jobs": "Jobs",
    "channels": "Channels",
    "agent_settings": "Settings",
    "global_agent": "Global agent",
    "new_room": "New room",
    "join_by_code": "Join by code",
    "sign_out": "Sign out",
    "login": "Sign in",
    "setup": "First-run setup",
    "agent_skills": "Skills",
    "agent_mcp": "MCP",
    "agent_memory": "Memory",
    "agent_channels": "Channels",
    "back_to_agents": "Back to agents",
    "terminal": "Terminal",
    "files": "Files",
    "config_files": "Config files",
    "linked_hubs": "Linked hubs",
    "workflows": "Workflows",
    "tools": "Tools",
    "secrets": "Secrets"
  },
  "common": {
    "loading": "Loading…",
    "retry": "Retry",
    "save": "Save",
    "cancel": "Cancel",
    "delete": "Delete",
    "more": "More",
    "rename": "Rename",
    "edit": "Edit"
  },
  "errors": {
    "not_implemented": "The hub declares {operation} in the contract but does not implement it yet.",
    "connection": "Could not reach the hub. Check the connection and try again.",
    "unexpected": "Something went wrong: {message}"
  },
  "login": {
    "title": "Sign in",
    "hub": "{name} · server {version}",
    "username": "Username",
    "password": "Password",
    "submit": "Sign in",
    "signing_in": "Signing in…",
    "setup_unknown": "Could not ask this hub whether it still needs first-run setup. Sign in, or reload the page.",
    "forgot": "Forgot password?",
    "forgot_waiting": "Waiting for your computer to confirm it is you…",
    "forgot_hint": {
      "macos": "Your Mac asks for Touch ID or an administrator’s password first.",
      "windows_hello": "Windows asks for Windows Hello first: your face, fingerprint or PIN.",
      "polkit": "Linux asks for your password first. It says it will run “true” as the administrator: that is only the check, nothing else runs.",
      "test": "A test confirmation answers instead of the operating system."
    },
    "local_sign_in": "Sign in on this computer",
    "recovery": {
      "title": "Choose a new password",
      "confirmed": "Your computer confirmed it is you. This is the owner account of the hub on this computer.",
      "username_label": "The owner’s username",
      "username_hint": "Sign in with exactly this name: every letter and dot counts.",
      "password": "New password",
      "password_hint": "At least {min} characters.",
      "confirm": "Confirm the new password",
      "mismatch": "The two passwords are not the same.",
      "too_short": "The password must be at least {min} characters.",
      "signs_out": "Saving signs the owner out on other devices: phones and other computers must sign in again.",
      "save": "Save and sign in",
      "saving": "Saving…",
      "cancel": "Cancel",
      "errors": {
        "cancelled": "Your computer did not confirm it is you. Nothing changed.",
        "unavailable": "This computer has no way to confirm it is you, so the password cannot be reset here.",
        "rate_limited": "Too many attempts. Wait a few minutes and try again.",
        "expired": "The confirmation expired. Press “Forgot password?” again.",
        "invalid_password": "The hub refused the new password. Use at least {min} characters.",
        "no_owner": "This hub has no owner yet.",
        "owner_disabled": "The owner account is disabled.",
        "busy": "Your computer is already asking. Answer that prompt first.",
        "failed": "Something went wrong. Try again, or sign in with the password."
      }
    }
  },
  "setup": {
    "title": "Create the owner account",
    "intro": "This hub has no owner yet. Create the owner account here.",
    "checking": "Asking the hub…",
    "open_title": "Setup is open to whoever opens this page first — finish it now",
    "open_body": "Until the owner exists, anyone who reaches this hub can create it, so do it now. If someone else got there first, restart the hub with COREHUB_RESET_OWNER=1 and set it up again.",
    "open_remaining": "Time left: {time}",
    "closed_title": "Setup now needs the setup token",
    "closed_body": "The time in which setup was open to anyone has ended, or this hub is set to token only. Paste the token the hub wrote when it started. Restarting the hub opens setup again for a while, with no token, unless it is token only.",
    "where_title": "Where to find the setup token",
    "where_body": "The hub printed it once in its log when it started, and wrote it to setup-token.txt inside the data directory. On the reference stack:",
    "where_local": "Running the hub outside Docker: the file is in your DATA_DIR (./data/setup-token.txt by default). A new token is generated on every restart until this account exists.",
    "token": "Setup token",
    "token_hint": "Paste it exactly; it is compared as a whole and never stored in the browser.",
    "username": "Username",
    "username_hint": "Lowercase letters, digits, dot, dash or underscore — 2 to 40 characters.",
    "display_name": "Display name (optional)",
    "workspace_name": "Profile name (optional)",
    "workspace_hint": "The name of the first profile; \"Default\" when left empty.",
    "password": "Password",
    "password_hint": "{min} characters or more. It is never shown, logged or stored anywhere but as a hash.",
    "confirm": "Confirm the password",
    "mismatch": "The two passwords are not the same.",
    "submit": "Create the account and sign in",
    "creating": "Creating the account…",
    "unattended": "An unattended install can still set HUB_ADMIN_PASSWORD before the first boot; the hub then creates the owner itself and this screen never appears."
  },
  "shell": {
    "sidebar": "Main menu",
    "segments": "Sections",
    "workspace": "Profile",
    "open_menu": "Open menu",
    "close_menu": "Close menu",
    "segment_later": "{name} arrives with its module in a later phase.",
    "language_chip": "Switch UI language",
    "theme_chip": "Switch theme",
    "connection": {
      "connected": "Connected",
      "connecting": "Connecting…",
      "offline": "Offline"
    },
    "theme_next": "Theme: {next}",
    "in_profile": "Profile: {name}",
    "fold": "Collapse sidebar",
    "unfold": "Expand sidebar",
    "person_menu": "Your menu",
    "back_to_chats": "Back to chats",
    "no_profile_title": "No profile yet",
    "no_profile_body": "Your account has not been given a profile, so there is nothing to open yet. Ask an admin to give you one, then check again.",
    "no_profile_retry": "Check again"
  },
  "pane": {
    "resize": "Resize the side pane (arrow keys resize, Home collapses, End expands)",
    "collapse": "Collapse the side pane",
    "expand": "Expand the side pane",
    "close": "Close the side pane",
    "kind": {
      "tool": "Tool output",
      "code": "Code",
      "preview": "Preview",
      "artifact": "Artifact",
      "tasks": "Tasks"
    }
  },
  "sessions": {
    "untitled": "New chat",
    "filter": "Filter sessions",
    "all_profiles": "All profiles",
    "profile_filter": "Show chats from",
    "empty": "No sessions in this profile yet. Start one with New chat.",
    "none_match": "No session matches this filter.",
    "pin": "Pin",
    "unpin": "Unpin",
    "pinned": "Pinned",
    "archive": "Archive",
    "delete": "Delete",
    "reorder": "Reorder {title} (Space to pick up, arrows to move)",
    "confirm_delete": "Delete \"{title}\" and its messages?",
    "status": {
      "idle": "Idle",
      "running": "Running",
      "waiting": "Waiting for you"
    },
    "source": {
      "chat": "Chat",
      "global_agent": "Global agent",
      "room": "Rooms",
      "task": "Board tasks",
      "schedule": "Schedules",
      "workflow": "Workflows",
      "channel": "Channels",
      "cli": "Terminal",
      "api": "API"
    },
    "unarchive": "Unarchive",
    "none_archived": "Nothing archived.",
    "scope": {
      "label": "List scope",
      "active": "Active",
      "archived": "Archived",
      "all": "All"
    },
    "confirm_delete_body": "Its messages go with it, and there is no undo.",
    "rename": "Rename",
    "rename_label": "Session name",
    "retitle": "Retitle",
    "more": "More for {title}",
    "retitle_hint": "Let the hub name this chat again, from its own first turn.",
    "select": "Select",
    "select_all": "Select all",
    "select_none": "Clear selection",
    "select_one": "Select {title}",
    "selected_count": "{count} selected",
    "confirm_delete_many": "Delete {count} conversations?",
    "categories": {
      "new": "New category",
      "new_label": "Category name",
      "new_in_profile": "It is made in the profile {profile}, and everyone who can enter that profile sees it.",
      "create": "Create",
      "rename": "Rename category",
      "move_up": "Move up",
      "move_down": "Move down",
      "delete": "Delete category",
      "confirm_delete": "Delete the category \"{name}\"?",
      "confirm_delete_body": "Its chats stay: they go back to the list without a category.",
      "more": "More for {name}",
      "count": "{count} chats",
      "move_to": "Move to category",
      "move_title": "Move \"{title}\" to a category",
      "move_hint": "Categories of the profile {profile}.",
      "none": "No category",
      "current": "Current",
      "new_and_move": "New category…",
      "empty_group": "Drag a chat here, or use its menu.",
      "colour": "Colour",
      "colour_title": "Colour of {name}",
      "colour_none": "No colour",
      "colours": {
        "blue": "Blue",
        "green": "Green",
        "amber": "Amber",
        "red": "Red",
        "purple": "Purple",
        "pink": "Pink",
        "teal": "Teal",
        "slate": "Slate"
      }
    },
    "channels": {
      "telegram": "Telegram",
      "whatsapp": "WhatsApp",
      "other": "Channels: {name}",
      "readonly_banner": "A conversation from {channel} — read-only; replies are made in {channel}",
      "unreachable": "Couldn't read the channel conversations from Hermes just now; showing what was last read.",
      "untitled": "A {channel} conversation",
      "has_more": "Only the latest 500 messages of this conversation are shown.",
      "no_messages": "No text messages in this conversation yet.",
      "continue": {
        "button": "Continue in Core Hub",
        "title": "Continue this conversation in Core Hub",
        "hint": "A new chat in this profile starts with this conversation attached for the agent to read. The conversation on {channel} stays as it is.",
        "note": "What should the agent do with it? (optional)",
        "go": "Continue",
        "no_agent": "No agent in this profile can take it. Install one from Agents first."
      },
      "hide": "Hide from my list",
      "unhide": "Show again",
      "delete": "Delete from Hermes…",
      "delete_title": "Delete “{title}” from Hermes?",
      "delete_body": "The conversation and its messages are removed from Hermes for everyone, permanently. This cannot be undone. The chat in {channel} itself is not affected.",
      "delete_confirm": "Delete permanently",
      "hidden_toast": "“{title}” is hidden from your list",
      "hidden_toast_body": "Only you stop seeing it. “Show hidden chats” at the end of the list brings it back.",
      "show_hidden": "Show hidden chats ({count})",
      "hide_hidden": "Stop showing hidden chats",
      "actions": "Conversation actions",
      "action_failed": "Couldn't change the conversation",
      "show_older": "Older channel conversations",
      "load_older": "Load older messages",
      "picture": "Picture sent on the channel",
      "picture_gone": "A picture was sent here. Hermes keeps channel pictures for a day, and this one is gone.",
      "send": {
        "label": "Write into this {channel} conversation",
        "placeholder": "Write to {peer} on {channel}…",
        "send": "Send on {channel}",
        "hint": "Goes out on {channel} first, as “من كور هب (your name): …”, then to the agent, which answers there. The reply shows here when the agent finishes.",
        "from_hub": "{name} · from Core Hub",
        "you": "You · from Core Hub",
        "open_current": "Open the current conversation",
        "error_channel": "{channel} refused the message, so it did not reach the agent: {message}",
        "status": {
          "sending": "Sending on {channel}…",
          "posted": "Posted on {channel} · waiting for the agent to take it",
          "delivered": "With the agent · it answers when its current turn ends",
          "answering": "The agent is answering on {channel}…",
          "answered": "Answered on {channel}"
        },
        "failed": {
          "bridge_no_answer": "Posted on {channel}, but Hermes did not take it in time (its messaging gateway may be restarting). Sending again posts it on {channel} again.",
          "not_accepted": "Posted on {channel}, but Hermes refused to hand it to the agent.",
          "not_picked_up": "Posted on {channel}, but the agent never picked it up. Hermes’s log says why."
        },
        "unavailable": {
          "not_admin": "A conversation from {channel}. Only an admin can write into it from here; replies are made in {channel}.",
          "platform_unsupported": "A conversation from {channel} — read-only here for now. Writing from Core Hub works for Telegram and WhatsApp.",
          "hermes_not_managed": "A conversation from {channel} — read-only: this hub does not run Hermes, so it cannot write into it.",
          "bridge_offline": "A conversation from {channel} — writing from here is unavailable while Hermes’s messaging gateway is starting or stopped.",
          "not_current": "This {channel} chat has moved on to a newer conversation; a message from here would go there.",
          "no_route": "A conversation from {channel} — Hermes keeps no way back to this chat, so it cannot be written into from here."
        }
      }
    }
  },
  "search": {
    "placeholder": "Search titles and messages…",
    "none": "Nothing found."
  },
  "new_chat": {
    "pick_agent": "Pick the agent this chat talks to. The list is the hub's registry.",
    "no_agents": "No agent is enabled on this hub. Install or enable one on the Agents page.",
    "greeting": "What shall we start with?",
    "lede": "Pick an installed agent from the chips, choose a working folder if you like, then type.",
    "in_profile": "New chat in {name}. To start it in another profile, switch the profile at the top of the page.",
    "none_installed": "No agent is installed on this hub yet. Hermes ships with it; if it shows as not installed, check that it is running or install an agent from the Agents page.",
    "open_agents": "Open Agents"
  },
  "chat": {
    "pick_one": "Pick a session from the list, or start a new chat.",
    "empty": "No messages yet. Say something below.",
    "assistant": "Agent",
    "streaming": "typing…",
    "failed": "failed",
    "interrupted": "stopped",
    "thinking": "Thinking",
    "reasoning": "Reasoning",
    "copy_code": "Copy code",
    "open_in_pane": "Open in side pane",
    "location": "Location",
    "usage": "{input} in · {output} out",
    "resumed": "Reconnected; {replayed} missed event(s) replayed.",
    "resynced": "Reconnected; the transcript was reloaded from the hub.",
    "session_deleted": "This session was deleted.",
    "run_failed": "The run failed: {error} ({code})",
    "empty_title": "What shall we start with?",
    "no_provider": "No model provider is configured for this profile, so the agent has nothing to run on.",
    "no_provider_action": "Open Settings → Models → Defaults",
    "agent_help": {
      "goose_provider": "{name} has no model provider set up yet: its config file needs GOOSE_PROVIDER and GOOSE_MODEL.",
      "claude_auth": "{name} needs a sign-in or an Anthropic-compatible provider. Add an Anthropic provider under Models, or sign Claude Code in on the hub's computer.",
      "codex_auth": "{name} needs an OpenAI key or a ChatGPT sign-in. Add an OpenAI provider under Models.",
      "gemini_auth": "{name} needs a Google sign-in or a Gemini API key. Add a Google provider under Models.",
      "provider_auth": "{name} has no key or sign-in to answer with. Add a provider it can use under Models.",
      "sign_in": "{name} needs its own account sign-in.",
      "open_config_files": "Open {name}'s config files",
      "open_models": "Open Settings → Models",
      "open_sign_in": "Sign {name} in",
      "agent_said": "{name} said:"
    },
    "no_key": "The provider rejected the stored key, so the agent could not reach it.",
    "no_key_action": "Open Settings → Models",
    "quota_exhausted": "{provider} ran out of quota for {model}. Pick another model for this chat.",
    "quota_pick_model": "Choose another model",
    "no_capacity": "{provider} has no capacity for {model} right now. Try again in a moment, or pick another model for this chat.",
    "rate_limited": "{provider} is limiting requests to {model} right now. Try again in a minute, or pick another model for this chat.",
    "model_status": {
      "trying": "Trying {model}…",
      "waiting_capacity": "{model} has no capacity right now — asking again in {seconds}s",
      "waiting_capacity_now": "{model} has no capacity right now — asking again",
      "waiting_limit": "{model} is limiting requests — asking again in {seconds}s",
      "waiting_limit_now": "{model} is limiting requests — asking again"
    },
    "you": "You",
    "queued": "Queued",
    "seconds": "{seconds}s",
    "thought_for": "Thought for {seconds}s",
    "pick_one_body": "Every chat keeps its own agent, its folder and its history.",
    "copied": "Copied",
    "agent_menu": "Agent: {name} — continue with another one",
    "agent_of_session": "This chat's agent",
    "fork_explains": "Another agent copies this conversation into a new chat with it and leaves this one as it is. The model selector below changes only this chat.",
    "continue_with": "Continue with {name}",
    "no_other_agent": "No other agent is installed.",
    "copy": "Copy",
    "reply": "Reply to this",
    "fork_here": "Fork from here",
    "time_yesterday": "Yesterday {time}",
    "replying_to": "Replying to: {text}",
    "estimated": "≈ {amount}",
    "anchor_missing": "The message this search pointed to is no longer in this conversation.",
    "older_loading": "Loading older messages…",
    "older_start": "Beginning of the conversation",
    "older_failed": "Couldn’t load older messages.",
    "answered_by": "Answered by {model}",
    "fallback_note": "{failed} failed, so {model} answered",
    "fallback_reason": "Why: {reason}",
    "bar_more": "More"
  },
  "composer": {
    "label": "Message composer",
    "placeholder": "Message the agent…",
    "disabled": "The session is not ready.",
    "send": "Send",
    "stop": "Stop the run",
    "attach": "Attach files",
    "drop_here": "Drop files to attach them",
    "upload_failed": "upload failed",
    "remove_attachment": "Remove {name}",
    "busy_hint": "A run is active; your next message is queued behind it.",
    "agents": "Installed agents",
    "agent_pick": "Use {name} for this chat",
    "agent_current": "Current agent: {name}",
    "agent_new_chat": "Start a new chat with {name}",
    "more": "Add to the message",
    "upload": "Upload a file",
    "drop_hint": "Or drop files straight onto the composer.",
    "model": "Model",
    "model_default": "Default model",
    "model_default_named": "Default · {model}",
    "model_small_context": "small context (under {tokens}K)",
    "model_agent_default": "Agent's own default",
    "approval": "Approvals",
    "approval_no_agent": "Pick an agent first.",
    "approval_unsupported": "This agent declares no approval mode.",
    "approval_mode": {
      "ask": "Default: ask",
      "auto_safe": "Auto-edit",
      "auto_all": "No ask",
      "off": "No approvals",
      "always": "Always ask",
      "manual": "Manual: asks about every dangerous command",
      "smart": "Smart"
    },
    "add_agent": "Add an agent",
    "approval_hint": {
      "ask": "Asks before a command or a write outside the working folder.",
      "always": "Asks before everything, reads included — slowest, safest.",
      "auto_safe": "Runs what it judges safe, asks about the rest.",
      "auto_all": "Runs everything without asking; nothing to object to.",
      "off": "No gate at all: commands, files and network without permission.",
      "manual": "Asks before every dangerous command.",
      "smart": "A helper model lets the safe ones through and asks about the rest."
    },
    "effort": {
      "label": "Thinking effort",
      "default": "Automatic",
      "none": "None",
      "minimal": "Minimal",
      "low": "Low",
      "medium": "Medium",
      "high": "High",
      "max": "Max"
    },
    "context_ring": "Context: {percent}% — {used} of {window} tokens",
    "placeholder_agent": "Message {agent}…"
  },
  "slash": {
    "title": "Commands",
    "skills_title": "Skills — pick one to use in this message",
    "no_match": "No command matches. Press Enter to send it as a message.",
    "needs_argument": "{command} needs words after it.",
    "model_unknown": "No model called “{model}” in this profile.",
    "cleared": "The screen was cleared; the messages are still in the conversation.",
    "show_cleared": "Show them",
    "describe": {
      "compress": "Compress this conversation’s context now (add words to say what to keep)",
      "steer": "Guide the running turn without stopping it",
      "skill": "Use one of the agent’s skills in this message",
      "plan": "Have the agent write a plan without doing the work",
      "goal": "Set a standing goal the agent works on across turns, or show it",
      "learn": "Turn the recent work, or what you describe, into a skill",
      "new": "Start a new chat with this agent",
      "fork": "Fork this conversation into a new one",
      "archive": "Archive this conversation",
      "model": "Change the model (add a name, or pick from the list)",
      "clear-screen": "Clear the screen; nothing is deleted"
    }
  },
  "context_meter": {
    "title": "Context window",
    "percent": "{percent}% full",
    "used": "{used} of {window} tokens",
    "ring_estimate": "Context (estimate): {percent}% — about {used} of {window} tokens",
    "source": {
      "reported": "As the agent reported it after the last turn.",
      "agent_estimate": "The agent’s own rough count after the last turn.",
      "estimate": "Estimate: the last turn’s tokens against the model’s window in the catalogue."
    },
    "compress": "Compress now",
    "compressing": "Compressing…",
    "compressing_manual": "Compressing the conversation’s context…",
    "compressing_auto": "The agent is compressing the context to make room…",
    "last_compressed": "Last compression: about {before} → {after} tokens.",
    "last_done": "Last compression finished.",
    "last_failed": "The last compression failed.",
    "wait_for_run": "Wait for the running turn to finish, then compress.",
    "breakdown_title": "What fills it",
    "breakdown_note": "By category, as the agent counts it roughly.",
    "category": {
      "system_prompt": "System prompt",
      "tool_definitions": "Tool definitions",
      "rules": "Rules",
      "skills": "Skills",
      "mcp": "MCP",
      "subagent_definitions": "Subagent definitions",
      "memory": "Memory",
      "conversation": "Conversation"
    }
  },
  "compression_settings": {
    "title": "Context compression",
    "subtitle": "How Hermes shortens long conversations in this profile. Applied from the next message.",
    "enabled": "Compress automatically",
    "enabled_hint": "When a conversation fills the window up to the threshold, Hermes summarises its older part.",
    "threshold": "Threshold (% of the window)",
    "threshold_hint": "Hermes raises anything under 75% to 75% for models whose window is under 512K tokens.",
    "target": "Kept word for word (% of the threshold)",
    "target_hint": "The recent part left as it is. Hermes keeps it between 10% and 80%.",
    "protect_first": "First messages always kept",
    "protect_last": "Recent messages always kept",
    "context_length": "Context window override (tokens)",
    "context_length_hint": "Only when the model’s own window is reported wrong. Empty = the model’s own.",
    "saved": "Saved. Hermes applies it from the next message.",
    "invalid": "Enter a whole number in range (at least 1024 for the window)."
  },
  "approval": {
    "from": "{agent} is waiting for your decision",
    "once": "Once",
    "session": "This session",
    "always": "Always",
    "deny": "Deny",
    "answer": "Your answer",
    "send": "Answer",
    "skip": "Skip (deny)",
    "waiting": "Waiting on you",
    "answer_hint": "Type your answer…"
  },
  "pending": {
    "open": "Waiting for you",
    "open_count": "Waiting for you: {count}",
    "title": "Waiting for you",
    "close": "Close",
    "none": "Nothing is waiting for you.",
    "none_body": "Approvals, questions from agents and workflow steps that need you show here.",
    "open_item": "Open where it is",
    "workflow_step": "Workflow step",
    "pairing": "{name} wants to message the agent on {platform}",
    "pairing_hint": "Approve or deny them here, or under Approvals on the agent’s channels page.",
    "global_agent": "Ask the global agent",
    "write_hint": "The agent wants to keep this; it waits for review.",
    "room_item": "Room"
  },
  "global_agent": {
    "intro": "One standing conversation with your agent in this profile, outside the chats list. Reach it from search and from the bar of things waiting for you.",
    "opening": "Opening the global agent…",
    "no_agent": "No agent is installed in this profile yet, so there is no one to talk to."
  },
  "tool": {
    "activity": {
      "steps": {
        "zero": "No steps",
        "one": "1 step",
        "two": "2 steps",
        "few": "{count} steps",
        "many": "{count} steps",
        "other": "{count} steps"
      },
      "failed": {
        "zero": "none failed",
        "one": "1 failed",
        "two": "2 failed",
        "few": "{count} failed",
        "many": "{count} failed",
        "other": "{count} failed"
      },
      "earlier": {
        "zero": "No earlier steps",
        "one": "+1 earlier step",
        "two": "+2 earlier steps",
        "few": "+{count} earlier steps",
        "many": "+{count} earlier steps",
        "other": "+{count} earlier steps"
      },
      "hide_earlier": "Hide the earlier steps",
      "seconds": "{seconds}s",
      "minutes": "{minutes}m {seconds}s"
    },
    "status": {
      "running": "running",
      "succeeded": "done",
      "failed": "failed",
      "interrupted": "stopped",
      "awaiting_approval": "awaiting approval"
    },
    "truncated": "output truncated",
    "arguments": "Arguments",
    "result": "Result"
  },
  "agents": {
    "empty": "The registry is empty.",
    "install": "Install",
    "remove": "Remove",
    "restart": "Restart",
    "restart_named": "Restart {name}",
    "restart_done": "Restarted",
    "restart_failed": "The restart failed",
    "limited": "This agent runs through the process harness and is limited.",
    "needs_setup": "Needs a provider or sign-in",
    "model_source": {
      "hub": "Models: Core Hub's providers",
      "agent": "Models: the agent's own account"
    },
    "update_available": "Update available: {version}",
    "under_agent": "Under the agent",
    "status": {
      "available": "Available",
      "not_installed": "Not installed",
      "installing": "Installing…",
      "updating": "Updating…",
      "error": "Error",
      "limited": "Limited",
      "disabled": "Disabled"
    },
    "capability": {
      "approvals": "Approvals",
      "channels": "Channels",
      "jobs": "Jobs",
      "kanban": "Tasks",
      "mcp": "MCP",
      "memory": "Memory",
      "plugins": "Plugins",
      "resume": "Resume",
      "skills": "Skills",
      "streaming": "Streaming",
      "tasks": "Tasks",
      "tools": "Tools",
      "worktrees": "Worktrees",
      "vision": "Vision",
      "audio": "Audio",
      "presets": "Presets",
      "journey": "Journey",
      "compress": "Context compression",
      "steer": "Run steering",
      "goals": "Goals",
      "plans": "Plans",
      "learn": "Learning",
      "skill_commands": "Skill commands",
      "config_files": "Config files"
    },
    "settings_of": "{name} settings",
    "settings_none": "This agent declares no settings.",
    "restart_required": "Changing these restarts the agent.",
    "settings": "Settings",
    "field_hint": {
      "list": "One item per line.",
      "json": "Valid JSON."
    },
    "updates": "Updates",
    "version_now": "Installed: {version}",
    "up_to_date": "No update available.",
    "check_update": "Check for updates",
    "update_now": "Update now",
    "auto_update": "Update automatically",
    "auto_update_hint": "The hub looks for a newer version every six hours and installs it only while the agent is idle; a run started during the update waits for it. Off by default.",
    "signin_title": "Sign in to the {name} account",
    "signin_hint": "{name} uses its own account rather than a provider key. Signing in here stores it for every profile on this hub.",
    "signin_action": "Sign in",
    "signin_approved": "Signed in. New conversations with {name} use this account.",
    "version_tested": "Tested version: {version}",
    "newer_than_tested": "Newer than the tested version",
    "below_minimum": "This Hermes ({version}) is older than {minimum}, the oldest version Core Hub is tested with. It still runs, but some features may not work until it is updated.",
    "update_hermes": "Update Hermes",
    "update_hermes_title": "Update the Hermes on this computer?",
    "update_hermes_body": "Core Hub will run Hermes's own updater (hermes update --yes) on the Hermes installed on this computer. It updates Hermes for everything else that uses it too, and then restarts the Hermes Core Hub runs.",
    "newer_than_tested_hint": "This version is past the one Core Hub was tested with ({version}). It should work; if something breaks, say so.",
    "update_available_untested": "Update available: {version} — newer than the tested version",
    "hermes_newer_than_tested": "This Hermes ({version}) is newer than {tested}, the version Core Hub is tested with. It may not be supported yet: some features may not work with it.",
    "hermes_update_available": "Hermes {version} is available (this computer has {current}).",
    "hermes_update_tested": "Core Hub is tested with this version.",
    "hermes_update_untested": "Core Hub is not tested with this version yet (it is tested with {tested}); some features may not work with it.",
    "tools": {
      "not_supervised": "This hub does not run Hermes itself, so it cannot ask Hermes to do this.",
      "refused": "Hermes refused: {message}",
      "api_unavailable": "Hermes's API did not answer: {message}",
      "profile_absent": "Hermes has no profile named {profile} yet, so it has no files to show here.",
      "runtime_absent": "Hermes is not installed or running here, so it has no folder to read."
    },
    "sections": "{name} sections",
    "open_section": "{section} · {name}",
    "not_found": "No agent with this id in this profile.",
    "gateways": {
      "title": "Messaging gateways",
      "default": "default profile",
      "no_channels": "no channels",
      "state": {
        "running": "running",
        "starting": "starting",
        "stopped": "stopped",
        "error": "failing"
      },
      "jobs_n": "{count} scheduled jobs"
    },
    "applies_next_message": "Applies from the next message.",
    "saved": "Saved.",
    "saved_next_message": "Saved in Hermes's settings; applies from the next message.",
    "saved_restart_needed": "Saved in Hermes's settings; applies once Hermes restarts.",
    "saved_restarting": "Saved; Hermes is restarting now so it applies.",
    "default_is": "Hermes's default: {value}",
    "default_none": "none",
    "default_on": "on",
    "default_off": "off",
    "use_default": "Hermes's default ({value})",
    "pending": {
      "title": "Waiting for review",
      "subtitle": "What the agent wrote to its memory or its skills, not saved yet",
      "empty": "Nothing is waiting for review.",
      "kind_memory": "Agent's memory",
      "kind_user": "What it knows about you",
      "kind_skill": "Skill",
      "origin_review": "Hermes's own review",
      "origin_chat": "During a conversation",
      "approve": "Approve",
      "reject": "Reject"
    }
  },
  "jobs": {
    "status": {
      "queued": "Queued",
      "running": "Running…",
      "succeeded": "Done",
      "failed": "Failed",
      "cancelled": "Cancelled"
    }
  },
  "settings": {
    "tools": "Tools",
    "back": "Back to Settings",
    "global_note": "This screen is hub-wide, outside the current profile.",
    "management": "Management",
    "management_hint": "Configured once: whole pages, not tabs.",
    "about": {
      "agent_manager": "Install, update and configure each agent",
      "models": "Providers, keys, models and voice",
      "device_connections": "Pair a phone and trusted devices",
      "knowledge": "Journal, notes and files"
    }
  },
  "account": {
    "username": "Username",
    "display_name": "Display name",
    "role": "Role",
    "workspaces": "Profiles",
    "field": "Field",
    "value": "Value",
    "password": "Password",
    "password_hint": "This device stays signed in; your other devices are signed out.",
    "current_password": "Current password",
    "new_password": "New password",
    "confirm_password": "Type the new one again",
    "password_mismatch": "Does not match the new one",
    "password_changed": "Password changed, and your other devices were signed out.",
    "change_password": "Change password"
  },
  "roles": {
    "owner": "Owner",
    "admin": "Admin",
    "member": "Member"
  },
  "display": {
    "theme": {
      "label": "Theme",
      "system": "System",
      "light": "Light",
      "dark": "Dark"
    },
    "glass": {
      "0": "Solid",
      "1": "Light",
      "2": "Medium",
      "3": "Full",
      "label": "Glass intensity",
      "hint": "Translucency and blur of the floating chrome only; content stays solid."
    },
    "language": "Language",
    "text_scale": "Text size",
    "local_only": "Saved on this device only; the hub could not store the preference.",
    "reasoning": {
      "label": "Show the agent's reasoning",
      "hint": "A \"thought for …\" line above the reply, with the text behind a disclosure."
    }
  },
  "theme": {
    "intro": "Theme and background of this device. The footer chip changes the same preference.",
    "reduced_note": "If your system asks for reduced transparency or motion, the app honours it automatically."
  },
  "devices": {
    "pair_intro": "Pair a phone: scan the QR with the Core Hub app, or type the code. The code expires in five minutes.",
    "pair": "Pair a device",
    "pair_again": "New pairing",
    "expires_in": "Expires in {seconds} s",
    "pairing_expired": "The pairing expired. Start a new one.",
    "pairing_cancelled": "The pairing was cancelled.",
    "pair_link": "On a computer with the Core Hub app, open this link there, or paste it into the app:",
    "copy_link": "Copy link",
    "copied": "Copied",
    "list_empty_title": "No devices yet",
    "list_empty_body": "Pair a phone or computer above, or turn on browser notifications under Settings → Notifications.",
    "never_seen": "No activity yet",
    "online": "Online",
    "this_browser": "This browser",
    "this_device": "This device",
    "owner_other": "Another person's",
    "rename": "Rename",
    "rename_title": "Rename the device",
    "name_label": "Name",
    "revoke": "Remove",
    "revoke_title": "Remove {name}?",
    "revoke_body": "It stops receiving notifications. A paired app is signed out and has to be paired again.",
    "test_push": "Send a test",
    "test_sent": "Sent. It should appear on the device in a moment.",
    "test_failed": "Not delivered: {error}",
    "kind": {
      "phone": "Phone",
      "tablet": "Tablet",
      "computer": "Computer",
      "browser": "Browser"
    },
    "push": {
      "title": "Push senders",
      "intro": "Web Push (browsers and the desktop app) works with nothing to set up. Android (FCM) and iPhone (APNs) need the owner's credentials.",
      "provider": {
        "webpush": "Web Push",
        "fcm": "FCM (Android)",
        "apns": "APNs (iPhone)"
      },
      "state": {
        "ready": "Ready",
        "disabled": "Switched off",
        "not_configured": "Not configured",
        "error": "Error"
      },
      "source": {
        "generated": "Keys made by the hub",
        "environment": "From the environment",
        "settings": "From Settings",
        "relay": "Through the Core Hub relay",
        "none": "None"
      },
      "devices": "Devices: {count}",
      "set_up": "Set up",
      "change": "Change",
      "form": {
        "fcm_intro": "Firebase Console → Project settings → Service accounts → Generate new private key. Paste the whole JSON file.",
        "apns_intro": "Apple Developer → Certificates, IDs & Profiles → Keys: a key with Apple Push Notifications service (APNs).",
        "enabled": "Send through this service",
        "service_account": "Service account JSON",
        "service_account_hint": "Stored encrypted and never shown again.",
        "secret_kept": "Leave empty to keep the stored one.",
        "key_id": "Key ID",
        "team_id": "Team ID",
        "bundle_id": "App bundle ID",
        "environment": "Environment",
        "production": "Production",
        "sandbox": "Sandbox (development builds)",
        "private_key": ".p8 key",
        "private_key_hint": "The contents of AuthKey_….p8. Stored encrypted and never shown again.",
        "forget": "Forget",
        "refused": "The hub refused it: {detail}",
        "drop_here": "or drop the file here",
        "paste_instead": "Paste instead",
        "paste_hide": "Hide the text field",
        "fcm_upload": "Upload the service-account JSON",
        "fcm_ok": "Service account for project {project}.",
        "fcm_not_json": "That file is not JSON. Choose the service-account key Firebase downloaded.",
        "fcm_google_services": "That is google-services.json, the Android app's own file. The hub needs a service-account key: Firebase Console → Project settings → Service accounts → Generate new private key.",
        "fcm_not_service_account": "That JSON is not a service-account key. Generate one under Firebase Console → Project settings → Service accounts.",
        "fcm_missing": "That service account is missing type, project_id, client_email or private_key. Download a new key from Firebase.",
        "apns_upload": "Upload AuthKey_….p8",
        "apns_ok": "Key read. Its Key ID was taken from the file name.",
        "apns_not_a_key": "That is not a .p8 key: it should start with -----BEGIN PRIVATE KEY-----.",
        "apns_not_p8": "That is not the .p8 file. Apple lets you download it once, as AuthKey_<Key ID>.p8."
      },
      "manage": "Manage",
      "hide": "Hide",
      "env_title": "Or set it with environment variables",
      "env_body": "Variables set on the hub win over what is saved here. The full list is in",
      "saved": "Saved",
      "saved_project": "project {project}",
      "saved_key": "key ending …{ending}",
      "saved_team": "team {team}",
      "saved_incomplete": "Saved, but not complete yet: open it again to fill in what is left.",
      "valid": {
        "fcm": "Saved. The service account looks valid; the first notification proves it with Google.",
        "apns": "Saved. The key looks valid (it signed a test token); the first notification proves it with Apple."
      }
    },
    "pair_heading": "Pair a phone or computer",
    "list_title": "Your devices",
    "list_title_admin": "Devices",
    "group": {
      "mobile": "Phones and tablets",
      "computer": "Computers",
      "browser": "Browsers"
    },
    "last_active": "Last active",
    "paired_on": "Paired {date}",
    "added_on": "Added {date}",
    "just_paired": "Just paired",
    "os": {
      "android": "Android",
      "ios": "iOS",
      "ipados": "iPadOS",
      "macos": "macOS",
      "windows": "Windows",
      "linux": "Linux"
    },
    "push_state": {
      "on": "Push on · {provider}",
      "not_in_build": "Push isn't available in this build of the app",
      "permission_pending": "Waiting for notification permission",
      "permission_denied": "Notifications are turned off on the device",
      "no_sender": "Push off: this hub has no {provider} sender yet",
      "off": "Push off"
    },
    "push_set_up": "Set up push",
    "pair_through_relay": "The phone will reach this hub at {url} (Reach from outside, in This device)."
  },
  "placeholder": {
    "later": "{name} arrives with its module in phase {phase}. Nothing is hidden here.",
    "agent": "Agent {id}"
  },
  "models": {
    "tab": {
      "general": "Providers",
      "auxiliary": "Defaults",
      "ensembles": "Ensembles",
      "stt_providers": "Speech to text",
      "tts_providers": "Text to speech",
      "images": "Images"
    },
    "providers": {
      "hint": "The providers you added. A shared one is added once and every profile uses it; a profile's own is used only in that profile, instead of a shared one of the same kind.",
      "none": "No providers yet. Add the first one and its models arrive with it."
    },
    "provider": {
      "key": "API key",
      "key_hint": "Paste the key",
      "key_stored": "[stored] — paste a new key to replace it",
      "save_key": "Save key",
      "clear_key": "Remove key",
      "configured": "Key stored",
      "not_configured": "No key",
      "no_key_needed": "No key needed",
      "test": "Test",
      "testing": "Asking the provider…",
      "refresh": "Refresh models",
      "enabled": "Enabled",
      "remove": "Remove",
      "add": "Add a provider",
      "label": "Name",
      "base_url": "Base URL",
      "model_count": "{count} models",
      "key_optional": "Key optional",
      "confirm_remove": "Remove the provider \"{label}\"?",
      "confirm_remove_body": "Its key and its model list go with it, and there is no undo.",
      "scope_shared": "Shared",
      "scope_only": "{profile} only"
    },
    "defaults": {
      "hint": "This profile's chat and coding models. Every agent here inherits them unless it is pinned to something else; a choice this profile has not made comes from the default profile.",
      "chat": "Chat",
      "none": "Not chosen",
      "inherited": "From the default profile",
      "no_models": "No models yet. Add a provider key and the hub fetches its model list.",
      "inheriting": "Agents inheriting these",
      "inheriting_hint": "What each agent will use when a chat does not name a model.",
      "fallbacks": "Fallback models",
      "fallbacks_hint": "Tried in order when the chat model fails because its provider is down, rate limited or not answering — never after a request the provider refused as invalid. Every agent of this profile uses them.",
      "fallbacks_empty": "No fallback models: if the chat model's provider fails, the turn fails.",
      "fallbacks_need_default": "Choose a chat model first; the fallbacks are tried after it.",
      "fallback_add": "Add a fallback model",
      "fallback_reorder": "Drag to reorder {model} (Space to pick up, arrows to move)",
      "fallback_instructions": "To reorder a fallback model, press Space or Enter on its handle, move it with the arrow keys, and press Space or Enter again to drop it. Escape puts it back.",
      "fallback_picked": "Picked up {model}, place {position} of {total}.",
      "fallback_moved": "{model} is now at place {position} of {total}.",
      "fallback_dropped": "{model} dropped at place {position} of {total}.",
      "fallback_cancelled": "Reordering cancelled; {model} is back at place {position} of {total}.",
      "fallback_remove": "Remove {model}"
    },
    "ensembles": {
      "hint": "Ensembles are created over the API for now; this tab lands with the rooms screens."
    },
    "test": {
      "ok": "The provider answered.",
      "http_error": "The provider answered with an error."
    },
    "refresh_all": "Refresh model cache",
    "more_models": "+{count} more",
    "row": {
      "provider": "Provider",
      "models": "Models"
    },
    "badge": {
      "builtin": "Built-in",
      "custom": "Custom",
      "default": "Current default",
      "disabled": "Switched off"
    },
    "action": {
      "set_default": "Set default provider",
      "display_names": "Display names",
      "visible_models": "Manage visible models",
      "edit": "Edit",
      "clear_credentials": "Clear credentials"
    },
    "panel": {
      "model": "Model",
      "alias": "Display name",
      "visible": "Visible",
      "alias_for": "Display name for {model}",
      "visible_for": "Show {model} in pickers",
      "no_models": "No models yet. Refresh the model list first.",
      "done": "Done",
      "visibility": "Visible models",
      "visibility_all": "Every model",
      "visibility_all_hint": "Including whatever the provider adds later.",
      "visibility_include": "Only the chosen ones",
      "visibility_include_hint": "Pick them in the table below.",
      "caption": "Models of {provider}",
      "alias_saved": "Display name saved."
    },
    "add": {
      "type": "Provider type",
      "type_preset": "Preset",
      "type_custom": "Custom",
      "select_provider": "Select provider",
      "where_key": "Where to get a key",
      "key_required": "API key",
      "key_optional": "API key (optional)",
      "show_key": "Show",
      "hide_key": "Hide",
      "default_model": "Default model",
      "model_placeholder": "Fetch the list first",
      "fetch": "Fetch",
      "fetching": "Asking the provider…",
      "fetched": "{count} models found.",
      "fetch_failed": "The provider did not return a model list.",
      "fetch_empty": "The provider answered, but listed no models.",
      "submit": "Add",
      "loopback": "This address is loopback, and the hub runs in a container — it would reach the container itself, not your machine. Try {url}, and on a Linux host add extra_hosts: [\"host.docker.internal:host-gateway\"] to the stack.",
      "scope": "Who is this provider for?",
      "scope_all": "All profiles",
      "scope_profile": "This profile only",
      "scope_all_hint": "Shared: every profile uses it, including profiles made later.",
      "scope_profile_hint": "Only {profile} uses it, and its key wins there over a shared provider of the same kind. Other profiles never see it.",
      "kind": "What it does",
      "kind_llm": "Chat",
      "kind_stt": "Speech to text",
      "kind_tts": "Text to speech",
      "sign_in_hint": "This provider is used by signing in to your account, not with a key. Add it, then sign in from its card.",
      "key_on_file": "This provider’s account already has a key saved here; it is used unless you type another.",
      "subscription_badge": "Subscription",
      "group_subscriptions": "Subscriptions",
      "group_keys": "With an API key",
      "hermes_sign_in_detail": "Sign in with a short code, through Hermes · for Hermes and Core Hub’s own agent"
    },
    "runtime": {
      "title": "Runtime",
      "hint": "What the agent runtime received from this profile.",
      "runtime_writable": {
        "ok": "The hub manages the agent runtime.",
        "missing": "No agent runtime this hub can configure."
      },
      "provider_keys": {
        "ok": "Provider keys are in the runtime's environment.",
        "missing": "A provider key has not reached the runtime."
      },
      "provider_verified": {
        "ok": "Every provider answered its last check.",
        "missing": "A provider refused its last check — open it and fix the key."
      },
      "model_selected": {
        "ok": "A chat model is selected for the runtime.",
        "missing": "No chat model is selected — choose one under Defaults."
      },
      "gateway_reloaded": {
        "ok": "The runtime restarted after the last change.",
        "missing": "Hermes did not restart after the last change. Restart it to apply the settings.",
        "scheduled": "Applying the change — Hermes restarts by itself in a moment.",
        "waiting_for_run": "Applying the change — Hermes restarts by itself once the reply in progress ends."
      },
      "ready": "Runtime ready · {passed}/{total} checks",
      "passed": "{passed} of {total} checks pass",
      "restart_now": "Restart now"
    },
    "speech": {
      "active_stt": "Speech to text for this profile",
      "active_tts": "Text to speech for this profile",
      "hint_stt": "The composer’s microphone and voice mode send what you say to this provider.",
      "hint_tts": "The speaker under each reply, reading replies aloud and voice mode speak through this provider.",
      "provider": "Provider",
      "none": "None",
      "model": "Model",
      "voice": "Voice",
      "voice_hint": "The provider’s voice id, e.g. alloy",
      "language": "Language (optional)",
      "save": "Save",
      "saved": "Saved.",
      "ready": "Ready",
      "not_ready": "Not ready",
      "try": "Preview",
      "try_failed": "The voice could not be played: {detail}",
      "add_hint": "Add Groq or OpenAI (they bring their speech rows with the same key), ElevenLabs, Deepgram, Azure Speech, or your own OpenAI-compatible speech server with Add provider → Custom.",
      "add_stt": "Add a speech-to-text provider",
      "add_tts": "Add a text-to-speech provider",
      "none_stt": "No speech-to-text provider yet. Speech usually has providers of its own; add one here.",
      "none_tts": "No text-to-speech provider yet. Speech usually has providers of its own; add one here.",
      "custom_value": "Typed by hand",
      "custom_group": "Typed by hand",
      "model_pick": "Pick a model",
      "model_id": "Model id",
      "model_custom": "…or type a model id",
      "language_auto": "Detect automatically",
      "lang_popular": "Popular languages",
      "lang_all": "All languages",
      "lang_all_count": "All languages ({count} voices)",
      "language_stt": "Spoken language (detected when empty)",
      "language_tts": "Language (optional)",
      "language_code": "Language code",
      "language_custom": "…or type a code, e.g. ar-EG",
      "gender_female": "Female",
      "gender_male": "Male",
      "gender_neutral": "Neutral",
      "any_language": "Any language",
      "voice_language": "Voice language",
      "voice_pick": "Pick a voice",
      "voice_id": "Voice id",
      "voices_loading": "Asking the provider for its voices…",
      "voices_documented": "From the provider’s documentation: it has no voice list to ask.",
      "voices_none": "This provider lists no voices: type the voice id.",
      "sample": "Preview language",
      "sample_ar": "مرحبًا، هكذا ستُقرأ الردود بهذا الصوت.",
      "sample_en": "Hello, this is how replies will sound in this voice."
    },
    "signin": {
      "signed_in": "Signed in",
      "signed_out": "Not signed in",
      "action": "Sign in",
      "again": "Sign in again",
      "title": "Sign in to {provider}",
      "steps": "Open the sign-in page, sign in to your account there, and enter this code:",
      "open": "Open the sign-in page",
      "code": "Code",
      "copy": "Copy the code",
      "waiting": "Waiting for you to approve it on the provider’s page…",
      "approved": "Signed in. Its models are being fetched.",
      "denied": "The sign-in was declined.",
      "expired": "The code ran out before it was used.",
      "failed": "The sign-in did not finish.",
      "retry": "Start again",
      "close": "Close",
      "link_steps": "Open the sign-in page and sign in to your account. Your browser then goes to an address that may not load — copy that whole address from the address bar and paste it here.",
      "paste_label": "The address your browser landed on",
      "paste_hint": "It starts with {start}",
      "paste_hint_any": "Paste the whole address from the browser’s address bar.",
      "paste_submit": "Finish signing in"
    },
    "images": {
      "hint": "The model this profile draws and edits images with. It comes from your chat providers — there are no separate image providers. A profile that chooses none uses the default profile's.",
      "model": "Image model",
      "none": "None of your chat providers offers an image model.",
      "none_hint": "Add or refresh a provider that has one — for example a proxy that serves gemini-3.1-flash-image, OpenAI with gpt-image, or a signed-in ChatGPT subscription — and it appears here.",
      "to_providers": "Go to Providers",
      "clear": "Use the default profile’s",
      "used_by": "Hermes's image tool and the image skills (generate, edit, remove background) use this model with its provider's key. Nothing else to set up.",
      "via_subscription": "Images via your {provider} subscription ({model} through the chat model)"
    },
    "catalogue": {
      "fallback": "This list is a fallback kept in code, not the provider's own: the provider could not be asked ({reason}). Refresh once it can be.",
      "from_account": "These are the models your {provider} plan offers, as the provider lists them for your account."
    },
    "subscription": {
      "intro": "Use a subscription you already pay for. Core Hub signs in to it, and its models work with every agent.",
      "unavailable": "Subscriptions cannot be signed in to on this hub: {reason}",
      "flow_device": "Sign in with a short code — works from a phone or a server",
      "flow_link": "Sign in with a link, then paste the address back",
      "has_windows": "Shows usage and reset times",
      "continue": "Continue to sign in",
      "legacy": "Signed in through Hermes (the older way). Core Hub’s gateway can hold this sign-in instead.",
      "move": "Move to Core Hub’s gateway",
      "move_title": "Move {provider} to Core Hub’s gateway",
      "move_body": "Core Hub’s gateway will hold this subscription, show its accounts and usage, and give its models to every agent. Your model choices that use it move over once you have signed in.",
      "move_no_copy": "A sign-in cannot be copied from Hermes, so you sign in once more. The current sign-in keeps working until you remove it.",
      "move_start": "Sign in through the gateway",
      "move_done": "Signed in. You can remove the old {provider} provider when you are ready.",
      "gemini_desktop_scope": "Gemini Desktop answers text only, so only Core Hub’s direct agent can use it — not Hermes or the coding agents. Each Core Hub chat stays one chat in your Gemini history.",
      "gemini_desktop_unofficial": "This uses the Gemini desktop app’s own sign-in and service, which Google does not offer to other apps; Google may change or limit it at any time."
    },
    "accounts": {
      "title": "Accounts",
      "open": "Accounts and usage",
      "count": "{count} ({ready} ready)",
      "description": "The accounts signed in under this subscription, their usage and recent errors.",
      "reload": "Refresh",
      "add_account": "Sign in another account",
      "unavailable": "The gateway could not be asked: {reason}",
      "none": "No account is signed in yet.",
      "errors": "Recent errors",
      "status": {
        "active": "Working",
        "cooling": "Waiting",
        "error": "Failing",
        "disabled": "Turned off",
        "refreshing": "Renewing",
        "unknown": "Unknown"
      },
      "enabled": "On",
      "retry_at": "Tried again",
      "no_windows": "This vendor reports no usage windows.",
      "no_windows_check": "No usage seen yet. Use “Check now” to ask the vendor.",
      "limit_reached": "The vendor says this account’s limit is reached.",
      "check_failed": "“Check now” read nothing: {reason}",
      "requests": "{success} answered · {failed} failed",
      "buckets": "Requests in the last 3 hours 20 minutes, 10 minutes a bar",
      "last_refresh": "Sign-in renewed",
      "email": "E-mail",
      "check": "Check now",
      "renew": "Renew sign-in",
      "remove": "Sign out",
      "remove_confirm": "Sign {account} out?",
      "remove_body": "The account is removed from Core Hub. Your session at the vendor is not revoked; you can do that on the vendor’s site.",
      "left": "{percent}% left",
      "resets": "Resets",
      "checked": "Checked",
      "observed": "From the last answer"
    }
  },
  "working_dir": {
    "label": "Working folder",
    "hint": "Where this chat works on disk.",
    "automatic": "Automatic folder",
    "automatic_note": "the hub names it after the session",
    "new": "New folder",
    "new_placeholder": "New folder name",
    "create": "Create",
    "locked": "The folder is fixed once the chat has run.",
    "button": "Folder: {name}"
  },
  "combobox": {
    "search": "Search…",
    "recent": "Recent",
    "loading": "Loading the list…",
    "unfetched": "Fetch the list first.",
    "none": "No models in this list.",
    "no_match": "Nothing matches “{query}”.",
    "error": "The list could not be fetched.",
    "count": "{count} models",
    "all": "All"
  },
  "ui": {
    "close": "Close",
    "dismiss": "Dismiss this notice",
    "breadcrumb": "Breadcrumb"
  },
  "queue": {
    "title": "Message queue",
    "send_now": "Send now",
    "steer": "Steer",
    "steer_hint": "Stop the live turn and take this instead, in the same context.",
    "remove": "Remove"
  },
  "tasks": {
    "project": "Project",
    "count": "{count} tasks",
    "new_task": "New task",
    "new_task_in": "New task in {name}",
    "add": "Add",
    "no_projects": "No project yet",
    "no_projects_body": "Tasks live inside a project. Create one to start.",
    "new_project": "New project",
    "project_name": "Project name",
    "rename": "Rename",
    "title_label": "Task title",
    "reorder": "Reorder {title}",
    "more": "More for {title}",
    "move_to": "Move to {column}",
    "blocked_why": "What is blocking it?",
    "reason": "Reason",
    "confirm_delete": "Delete “{title}”?",
    "confirm_delete_body": "This cannot be undone.",
    "status": {
      "triage": "Triage",
      "todo": "To do",
      "ready": "Ready",
      "scheduled": "Scheduled",
      "running": "Running",
      "blocked": "Blocked",
      "review": "Review",
      "done": "Done",
      "archived": "Archived"
    },
    "priority": {
      "urgent": "Urgent",
      "high": "High",
      "normal": "Normal",
      "low": "Low"
    },
    "columns": {
      "queue": "Queue",
      "waiting": "Waiting",
      "review": "Review",
      "done": "Done"
    },
    "intake_hint": "A task arrives here, is specified, and then joins the queue.",
    "nothing": "Nothing",
    "which_waiting": "Waiting on what?",
    "action": {
      "promote": "Ready",
      "archive": "Archive",
      "schedule": "Schedule",
      "block": "Blocked",
      "unblock": "Unblock",
      "requestReview": "For review",
      "reopenReview": "Reopen",
      "complete": "Done",
      "queue": "To the queue"
    },
    "confirm": {
      "archive": "Archive “{title}”?"
    },
    "all_projects": "All projects",
    "hermes": {
      "origin": "On Hermes's board",
      "refused": "Hermes said no: {message}",
      "owns_text": "This card is on Hermes's board, and this hub does not run Hermes here, so its title and description are Hermes's to change.",
      "owns_card": "This card is on Hermes's board, and this hub does not run Hermes here, so Hermes decides what happens to it.",
      "writes_through": "This card is on Hermes's board: changes are made on Hermes first.",
      "api_unavailable": "Hermes's API is not available right now, so nothing was changed: {message}",
      "confirm_delete_body": "It is deleted from Hermes's board too. This cannot be undone.",
      "history": {
        "title": "On Hermes",
        "runs": "Attempts",
        "events": "What happened",
        "no_runs": "Hermes has not worked on it yet.",
        "no_events": "Nothing recorded yet.",
        "attempt": "attempt {id}",
        "event": {
          "created": "Created",
          "assigned": "Assigned",
          "claimed": "Picked up by a worker",
          "spawned": "Worker started",
          "heartbeat": "Worker still working",
          "completed": "Completed",
          "blocked": "Blocked",
          "unblocked": "Unblocked",
          "commented": "Comment",
          "edited": "Edited",
          "reprioritized": "Priority changed",
          "status": "Moved",
          "archived": "Archived",
          "reclaimed": "Taken back from its worker",
          "timed_out": "Timed out",
          "review_requested": "Sent for review",
          "review_reopened": "Reopened after review",
          "changes_requested": "Changes requested",
          "scheduled": "Scheduled",
          "promoted": "Promoted",
          "promoted_manual": "Promoted by hand",
          "gave_up": "Given up",
          "attached": "File attached",
          "linked": "Linked",
          "unlinked": "Unlinked",
          "specified": "Specified"
        },
        "run": {
          "running": "Running",
          "done": "Done",
          "completed": "Completed",
          "blocked": "Blocked",
          "review": "In review",
          "review_requested": "Sent for review",
          "reclaimed": "Taken back",
          "crashed": "Crashed",
          "timed_out": "Timed out",
          "scheduled": "Scheduled",
          "changes_requested": "Changes requested"
        }
      }
    },
    "assign": {
      "open": "Assign to an agent…",
      "again": "Reassign…",
      "title": "Assign to an agent",
      "agent": "Agent",
      "instructions": "Instructions",
      "instructions_hint": "Optional. Sent to the agent with the task.",
      "start": "Assign and start",
      "only": "Assign only",
      "no_agents": "No agent on this hub can take a task yet. Install or enable one in Agents.",
      "not_started": "Handed to this agent's own board: it starts the task from there, not the hub.",
      "waiting": "This task depends on tasks that are not done yet: {titles}. “Assign and start” starts it now anyway."
    },
    "stop": "Stop",
    "unassign": "Unassign",
    "open_session": "Open the conversation",
    "show_archived": "Show archived ({count})",
    "hide_archived": "Hide archived ({count})",
    "waiting_on": "Waiting on {count}",
    "waiting_on_list": "Waiting on: {titles}",
    "stuck": "Stuck",
    "stuck_since": "No activity from its run since {time}",
    "details": {
      "open": "Details…",
      "title": "Task details",
      "description": "Description",
      "priority": "Priority",
      "waiting_on": "Waiting on",
      "waiting_hint": "It starts on its own only when all of these are done.",
      "comments": "Comments",
      "no_comments": "No comments yet.",
      "add_comment": "Add a comment",
      "send_comment": "Comment"
    },
    "handover": {
      "open": "Hand to another profile…",
      "title": "Hand to another profile",
      "workspace": "Profile",
      "submit": "Hand over",
      "hint": "Hermes gives the card to that profile's Hermes, and the card moves to that profile.",
      "no_workspaces": "There is no other profile to hand it to.",
      "confirm_running": "Hand over “{title}” while it runs?",
      "confirm_running_body": "Hermes stops its current run first, then hands the card over."
    },
    "project_settings": {
      "open": "Project settings",
      "title": "Project settings",
      "hint": "With a repository, each task you start gets its own git worktree on a branch of its own, and the agent works there.",
      "repository": "Repository path",
      "branch": "Base branch",
      "branch_hint": "New task branches start from it. Empty: the branch checked out in the repository."
    },
    "repo": {
      "hint": "A git repository inside this profile's folder. A relative path starts from that folder. Leave it empty for no repository.",
      "placeholder": "my-repo",
      "invalid": {
        "outside_root": "The repository must be inside this profile's folder: {message}",
        "not_found": "That folder does not exist: {message}",
        "symlink": "The path goes through a symbolic link, which is not allowed: {message}",
        "not_a_git_repo": "That folder is not a git repository: {message}"
      }
    },
    "auto_start": {
      "label": "Start automatically",
      "hint": "Starts on its own once it is in Ready, given to an agent, and everything it depends on is done. Stopping it turns this off."
    },
    "worktree": {
      "title": "Worktree",
      "branch": "Branch",
      "path": "Folder",
      "changes": "Changes",
      "counts": "{files} changed files · {ahead} commits ahead of {base}",
      "remove": "Remove worktree",
      "confirm_remove": "Remove this task's worktree?",
      "confirm_remove_body": "The folder is removed. The branch {branch} stays in the repository, and uncommitted changes are lost.",
      "running": "The task is running in this worktree. Stop it first.",
      "failed": "Git could not make the task's worktree: {message}",
      "no_repository": "This task's project has no repository. Set one in Project settings.",
      "status": {
        "creating": "Being made",
        "ready": "Ready",
        "dirty": "Has changes",
        "merged": "Merged",
        "removed": "Removed",
        "error": "Git refused"
      }
    },
    "count_one": "1 task",
    "dod": {
      "title": "Definition of done",
      "hint": "What must be true for the task to count as done. The agent is given these with every run.",
      "review_hint": "In review: tick each line that holds, then Save.",
      "ticked": "{done} of {total} ticked",
      "tick": "Holds: {text}",
      "remove": "Remove “{text}”",
      "line": "Line {number}",
      "placeholder": "e.g. The tests pass",
      "add": "Add a line",
      "card": "Definition of done: {done} of {total} ticked",
      "hermes_card": "This card is on Hermes's board, and Hermes briefs its own worker from the card, so it takes no definition of done or constraints here."
    },
    "constraints": {
      "title": "Constraints",
      "hint": "What the agent must keep to while it works — a file not to touch, a library not to add. Given with every run.",
      "line": "Constraint {number}",
      "placeholder": "e.g. Do not add new libraries",
      "add": "Add a constraint"
    },
    "bulk": {
      "select": "Select",
      "count": "{count} selected",
      "priority": "Set priority",
      "comment": "Comment on all",
      "comment_title": "One comment on {count} tasks",
      "refused": "Not changed: {count} (Hermes refused, or they are gone). Changed: {done}.",
      "tick": "Select “{title}”"
    },
    "new": {
      "name": "Title",
      "name_required": "Write a title for the task.",
      "description": "Description",
      "description_hint": "The details and instructions the agent works from. Markdown works here.",
      "who": "Who does it",
      "agent": "Give it to",
      "agent_none": "Nobody for now",
      "model": "Model",
      "model_default": "The agent's default",
      "model_hint": "Used when it starts now. A task that starts later runs on the agent's default model.",
      "hermes_note": "Hermes keeps this card on its own board and runs it from there, with its own model. Leave it in Triage to specify it first.",
      "details": "Details",
      "due": "Due",
      "due_past": "That moment has already passed.",
      "due_invalid": "That is not a date.",
      "tags": "Tags",
      "tags_hint": "Separate them with commas.",
      "project": "Project",
      "project_own": "The profile's own list",
      "when": "When",
      "start_now": "Start now",
      "start_now_hint": "The agent starts at once, in the task's own conversation.",
      "start_needs_agent": "Give it to an agent to start it now.",
      "start_later": "Not yet",
      "start_later_hint": "It waits on the board until someone starts it.",
      "status": "Where it waits",
      "status_hint": "Triage is for a task still being specified; Ready is next in line.",
      "create": "Create",
      "start_again": "Start again",
      "close": "Close",
      "not_started": "The task was created, but it did not start. Start again, or close and start it from its card."
    },
    "subtasks": {
      "title": "Subtasks",
      "hint": "Smaller steps inside the task. The agent is given them with the task.",
      "line": "Subtask {number}",
      "placeholder": "e.g. Draft the outline",
      "add": "Add a subtask"
    }
  },
  "schedules": {
    "new": "New schedule",
    "new_hint": "Saved with its next run computed. A schedule for Hermes runs in Hermes's own scheduler; any other is run by the hub itself.",
    "name": "Name",
    "kind": "Kind",
    "prompt": "What should the agent be asked?",
    "empty": "No schedules yet",
    "kinds": {
      "cron": "Cron expression",
      "interval": "Every so often",
      "once": "Once"
    },
    "value": {
      "cron": "Expression",
      "interval": "Every how many minutes",
      "once": "Date and time"
    },
    "cron_hint": "Five fields: minute hour day month weekday.",
    "every": "Every {minutes} minutes",
    "next": "Next: {at}",
    "never": "No next time",
    "enabled": "Enabled",
    "run_now": "Run now",
    "confirm_delete": "Delete “{name}”?",
    "state": {
      "scheduled": "Scheduled",
      "running": "Running",
      "paused": "Paused",
      "exhausted": "Finished"
    },
    "agent": "Agent",
    "delivers_to": "Posts to {channel}",
    "last_error": "Last run failed: {error}",
    "hermes": {
      "origin": "In Hermes's scheduler",
      "refused": "Hermes said no: {message}",
      "unreachable": "Hermes is not answering right now, so nothing was changed.",
      "timezone": "Hermes runs every cron in {zone}. Use that zone, or change Hermes's timezone.",
      "use_zone": "Use {zone}",
      "delivery": "Hermes posts to messaging platforms itself — ask for it in the prompt.",
      "prompt_required": "A schedule for Hermes needs a prompt.",
      "fired": "Hermes will run it on its next tick.",
      "run_options": "Hermes decides these two for its own jobs, so they are not set here."
    },
    "in_profile": "New schedules are made in {name}. To make one in another profile, switch the profile at the top of the page.",
    "options": {
      "show": "Run options",
      "hide": "Hide options",
      "title": "Run options",
      "missed": "Run if missed (within 24 hours)",
      "missed_hint": "If the hub was not running at its time, it runs once when the hub is back within 24 hours. Unchecked, it never runs late, and the history says so. Up to two minutes late still counts as on time.",
      "overlap": "If the previous run is still going",
      "overlap_hint": "\"Run now\" always starts at once and stops nothing.",
      "overlaps": {
        "skip": {
          "label": "Skip",
          "hint": "This time does not run, and the history says so."
        },
        "wait": {
          "label": "Wait, then run",
          "hint": "Runs as soon as the previous run ends. Only one time waits; any later one is skipped and recorded."
        },
        "parallel": {
          "label": "Run alongside",
          "hint": "Runs in a new conversation next to the previous one."
        },
        "replace": {
          "label": "Stop the previous",
          "hint": "Stops the previous run for real, then starts."
        }
      },
      "saved": "Saved the options of \"{name}\"."
    },
    "fired": "“{name}” started.",
    "target_unavailable": "It could not start: {message}",
    "history": {
      "show": "History",
      "hide": "Hide history",
      "empty": "It has not run yet.",
      "open_session": "Open the conversation",
      "open_run": "Open the run",
      "waiting": "Waiting for the previous run to end",
      "trigger": {
        "schedule": "On its time",
        "manual": "Run now"
      },
      "status": {
        "queued": "Queued",
        "running": "Running",
        "succeeded": "Done",
        "failed": "Failed",
        "cancelled": "Not run"
      }
    },
    "run": {
      "title": "Workflow run",
      "waiting": "This run is waiting for your approval.",
      "reason": "Reason (optional)",
      "approve": "Approve",
      "deny": "Deny",
      "status": {
        "queued": "Queued",
        "running": "Running",
        "waiting": "Waiting",
        "succeeded": "Done",
        "failed": "Failed",
        "cancelled": "Cancelled"
      },
      "step_status": {
        "pending": "Pending",
        "queued": "Queued",
        "running": "Running",
        "waiting_approval": "Waiting for approval",
        "succeeded": "Done",
        "failed": "Failed",
        "skipped": "Skipped",
        "cancelled": "Cancelled",
        "rejected": "Rejected"
      }
    },
    "templates": {
      "label": "Common schedules",
      "every_hour": "Every hour",
      "daily_8": "Every day at 8:00",
      "weekdays_9": "Weekdays at 9:00",
      "monday_9": "Every Monday at 9:00",
      "monthly_1": "First of the month at 9:00",
      "every_15": "Every 15 minutes"
    },
    "preview": {
      "title": "Next runs ({zone})",
      "incomplete": "Fill in the time to see when it runs.",
      "none": "It would not run again.",
      "cron_invalid": "The hub cannot read this: {message}",
      "timezone_unknown": "The hub does not know the timezone “{zone}”."
    },
    "limits": {
      "title": "Limits",
      "edit": "Change this workflow’s limits",
      "hide": "Hide the limits form",
      "time": "Time limit",
      "cost": "Cost limit",
      "step": "Step timeout",
      "spent": "Cost so far",
      "none": "No limit",
      "not_priced": "No priced turn yet",
      "form_hint": "For the next runs of this workflow. Leave a field empty for no limit. Time waiting for someone’s approval does not count.",
      "time_minutes": "Time limit for a run (minutes)",
      "cost_usd": "Cost limit for a run (US dollars, the hub’s estimate)",
      "step_minutes": "Timeout for each step (minutes)",
      "invalid_time": "A number of minutes above zero, at most a week.",
      "invalid_cost": "An amount in US dollars above zero, like 2.50.",
      "invalid_step": "A number of minutes above zero, at most a day.",
      "saved": "Limits saved. They apply from the next run.",
      "units": {
        "hours": "{n} h",
        "minutes": "{n} min",
        "seconds": "{n} s"
      },
      "stopped": {
        "max_duration": "Stopped: the run went over its time limit.",
        "max_cost": "Stopped: the run went over its cost limit.",
        "step_timeout": "Stopped: a step went over its timeout and nothing handled it."
      },
      "run_with": "Run with limits for this run",
      "run_title": "Limits for this run",
      "run_hint": "Starts from the workflow's limits. What you change applies to this run only; an empty field means no limit.",
      "save_first": "Save the workflow to set its limits."
    }
  },
  "workflows": {
    "section": "Workflows",
    "new": "New workflow",
    "new_hint": "Draw the steps and connect them. New workflows are made in {name}.",
    "empty": "No workflows yet",
    "empty_hint": "Make one, add steps from the palette and connect them.",
    "untitled": "Untitled workflow",
    "steps_count": "Steps: {count}",
    "runs_count": "Runs: {count}",
    "open": "Edit",
    "run": "Run",
    "duplicate": "Duplicate",
    "copy_name": "{name} (copy)",
    "confirm_delete": "Delete “{name}”? Its runs and its schedules go with it.",
    "started": "“{name}” started.",
    "open_run": "Show the run",
    "status": {
      "idle": "Idle",
      "running": "Running",
      "waiting": "Waiting",
      "error": "Error"
    },
    "activity": {
      "running_step": "Running · {step}",
      "waiting": "Waiting for approval",
      "waiting_step": "Waiting for approval · {step}"
    },
    "kinds": {
      "agent": "Agent",
      "condition": "Condition",
      "delay": "Delay",
      "notify": "Notify",
      "approval": "Approval"
    },
    "kind_hints": {
      "agent": "An agent works on a prompt",
      "condition": "Goes one way or the other",
      "delay": "Waits, up to an hour",
      "notify": "A notice in the inbox",
      "approval": "Waits for a person"
    },
    "routes": {
      "success": "On success",
      "failure": "On failure",
      "always": "Always"
    },
    "condition_routes": {
      "success": "Yes",
      "failure": "No",
      "always": "Always"
    },
    "states": {
      "idle": "Not reached yet",
      "waiting": "Waiting",
      "running": "Running",
      "done": "Done",
      "failed": "Failed",
      "skipped": "Skipped"
    },
    "editor": {
      "back": "Back to workflows",
      "name": "Workflow name",
      "unsaved": "Unsaved changes",
      "saved": "Saved.",
      "palette": "Add a step",
      "canvas_label": "Workflow canvas",
      "canvas_help": "Drag a step to move it. Drag from a step's green dot (success) or red dot (failure) onto another step to connect them. Keys: Tab moves between steps, arrows move the selected step, Delete removes it, + and − zoom, 0 fits everything.",
      "zoom_in": "Zoom in",
      "zoom_out": "Zoom out",
      "fit": "Fit",
      "legend": "Green: on success · Red: on failure · Grey: always",
      "no_selection": "Select a step to edit it, or add one from the palette.",
      "checking": "Checking…",
      "valid": "Ready to run",
      "problems": "Problems: {count}",
      "warnings": "Warnings: {count}",
      "save_blocked": "Fix the problems before saving.",
      "name_required": "Give the workflow a name before saving.",
      "check_failed": "The drawing could not be checked: {message}",
      "check_fields": "The drawing could not be checked: fix the marked fields.",
      "name_refused": "The name is not accepted: {message}",
      "fields_refused": "Not saved: fix the marked fields.",
      "delete_step": "Delete step",
      "delete_edge": "Delete connection",
      "connections": "Connections from this step",
      "no_connections": "Nothing follows this step yet.",
      "connect_target": "Next step",
      "connect_route": "When",
      "connect_add": "Connect",
      "edge_title": "Connection",
      "edge_label": "From “{from}” to “{to}”: {route}",
      "run_input": "Run input (optional)",
      "run_input_hint": "Steps read it as {input}.",
      "run_from_here": "Run from this step",
      "rerun_from_here": "Run again from this step",
      "rerun_hint": "A new run starts here, reusing what the earlier steps said in this run.",
      "save_first": "Saves the workflow, then runs it.",
      "mode_edit": "Edit",
      "mode_runs": "Runs",
      "runs_empty": "It has not run yet. Run it to see each step here.",
      "run_pick": "Run",
      "run_status": "Run status",
      "run_of": "{status} · {at}",
      "cancel_run": "Stop the run",
      "step_output": "Output",
      "step_output_show": "Show the whole output",
      "step_error": "Error",
      "step_not_reached": "This run did not reach this step.",
      "run_readonly": "A run shows the drawing it ran; switch to Edit to change it.",
      "issue_count": "{count} findings",
      "run_find": "Find a run",
      "run_find_hint": "Task or event id",
      "run_filtered": "Filtered: not one to act on",
      "run_task": "Task {id}",
      "run_event": "Event {id}",
      "panel_failed": "This part of the editor hit an error and was closed so the rest keeps working: {message}",
      "panel_retry": "Show it again"
    },
    "form": {
      "title": "Title",
      "id": "Step id",
      "id_hint": "Later steps read this step's output as {path}.",
      "agent": "Agent",
      "agent_none": "Choose an agent",
      "model": "Model (optional)",
      "model_default": "The agent's own model",
      "prompt": "Prompt",
      "insert": "Insert",
      "insert_input": "The run's input",
      "insert_step": "Output of “{name}”",
      "insert_none": "Connect earlier steps to use their output here.",
      "attachments_note": "Workflow steps cannot carry attachments yet.",
      "condition_path": "What to check",
      "condition_path_hint": "A value the run can read: input, steps.<id>.output or trigger.<field>.",
      "condition_operator": "Comparison",
      "condition_value": "Compare with",
      "condition_text": "Condition",
      "condition_raw_hint": "The form cannot split this condition into parts; edit it as text.",
      "condition_routes_hint": "Yes follows the success connections; no follows the failure ones.",
      "operators": {
        "eq": "is",
        "ne": "is not",
        "gt": "greater than",
        "gte": "at least",
        "lt": "less than",
        "lte": "at most",
        "contains": "contains",
        "matches": "matches (regular expression)",
        "exists": "exists",
        "empty": "is empty"
      },
      "delay_amount": "Wait",
      "delay_unit": "Unit",
      "seconds": "Seconds",
      "minutes": "Minutes",
      "delay_hint": "Up to an hour. A longer wait belongs in a schedule.",
      "delay_template": "The wait is read from {value} when the step runs.",
      "notify_text": "Message",
      "notify_to": "Goes to the inbox of whoever runs the workflow.",
      "approval_question": "Question",
      "approval_who": "Anyone who can answer approvals in this profile approves or denies it — here, in the inbox, or from the pending bar.",
      "approval_timeout": "It waits until someone answers; there is no time limit.",
      "approval_required": "Ask for approval before this step runs",
      "rules_toggle": "Several rules",
      "rules_match": "Say yes when",
      "rules_all": "every rule holds",
      "rules_any": "any one rule holds",
      "rules_remove": "Remove this rule",
      "rules_add": "Add a rule",
      "rules_hint": "Each rule compares a path — trigger.event, trigger.task_id, trigger.body.…, steps.<id>.output — with a value. Yes follows the green connections, no the red ones; a no with nothing after it ends the run as “filtered”."
    },
    "issues": {
      "field_invalid": "The hub did not accept “{detail}” as written.",
      "node_id_missing": "A step has no id.",
      "node_id_duplicate": "Two steps share this id.",
      "edge_from_unknown": "This connection starts at a step that does not exist.",
      "edge_to_unknown": "This connection ends at a step that does not exist.",
      "condition_unreadable": "The condition cannot be read ({detail}).",
      "delay_out_of_range": "The wait must be 0 to {detail} seconds.",
      "template_root_unknown": "It reads {detail}, but a step can only read input, trigger or steps.",
      "template_step_unknown": "It reads {detail}, but there is no such step.",
      "workflow_empty": "The workflow has no steps yet.",
      "no_start": "Every step has a step before it, so the workflow has no starting point.",
      "many_starts": "{detail} steps have nothing before them; they all start together.",
      "agent_missing": "This step names no agent.",
      "rule_path_invalid": "A rule has no readable path (letters, digits, _ and dots).",
      "rule_operator_invalid": "A rule uses an operator the hub does not know.",
      "rule_value_missing": "A rule compares with nothing: give it a value.",
      "rule_regex_invalid": "A rule’s pattern is not a valid regular expression.",
      "send_no_target": "This step sends nowhere: choose Telegram or a conversation.",
      "send_platform_unknown": "Target {detail} names a platform this hub cannot send to.",
      "send_chat_missing": "Target {detail} has no Telegram chat id.",
      "send_formatting_unknown": "Target {detail} names a Telegram formatting this hub does not know (plain, HTML or MarkdownV2).",
      "send_conversation_missing": "Target {detail} names no conversation.",
      "conversation_mode_unknown": "This step’s conversation mode is not one this hub knows.",
      "conversation_id_missing": "Choose the conversation this step talks in, or paste its id.",
      "conversation_id_invalid": "This step’s conversation id is not a conversation id."
    },
    "triggers": {
      "title": "Triggers",
      "hint": "An address on this hub that another service (ClickUp, GitHub…) sends events to. Each event that passes the signature check and the event filter starts a run with the event as {{trigger.…}}. The hub must be reachable from the internet.",
      "save_first": "Save the workflow first, then add a trigger.",
      "preset": "Sender",
      "add": "Add trigger",
      "presets": {
        "clickup": "ClickUp",
        "github": "GitHub",
        "generic_hmac": "Signed webhook (HMAC)",
        "token": "Webhook with a token"
      },
      "enabled": "On",
      "url": "Address",
      "url_hint": "Give this address to the sender. It stays the same.",
      "copy": "Copy",
      "copied": "Copied",
      "secret": "Secret",
      "secret_hint": {
        "clickup": "Register the webhook in ClickUp with the address above; paste the “secret” from ClickUp’s answer here.",
        "github": "The secret you typed in the GitHub webhook’s settings.",
        "generic_hmac": "The secret the sender signs the body with (HMAC-SHA256).",
        "token": "The token the sender puts in the header."
      },
      "secret_stored": "A secret is stored. It is never shown; typing a new one replaces it.",
      "secret_missing": "No secret yet: every delivery is refused until one is stored.",
      "header": "Header",
      "prefix": "Prefix before the signature",
      "encoding": "Encoding",
      "events": "Events it takes",
      "events_hint": "None chosen takes every event.",
      "test_event": "Test event",
      "test": "Send test event",
      "status": {
        "received": "Received",
        "duplicate": "Repeat, ignored",
        "signature_rejected": "Signature refused",
        "filtered_out": "Event not taken",
        "run_started": "Run started",
        "run_succeeded": "Run succeeded",
        "run_failed": "Run failed"
      },
      "deliveries": "Deliveries (last 7 days)",
      "no_deliveries": "Nothing has arrived yet.",
      "filtered": "filtered",
      "test_badge": "test",
      "open_run": "Open the run",
      "delete": "Delete trigger",
      "delete_title": "Delete this trigger?",
      "delete_body": "Its address stops answering and its delivery log is deleted. The sender keeps sending until you remove the webhook there too."
    },
    "send": {
      "title": "Send message",
      "hint": "Sends the step’s words to Telegram and/or a Core Hub conversation.",
      "message": "Message",
      "targets": "Send to",
      "telegram": "Telegram",
      "chat_id": "Telegram chat id",
      "telegram_hint": "A chat, user or group id (a group is like -1001234567890). Sent by this profile’s own Telegram bot; long text is split into several messages.",
      "conversation": "A Core Hub conversation",
      "conversation_hint": "Posted in the conversation, so it shows in every app. If the conversation is deleted, a new one with the same title is made and the step points to it.",
      "pick": "Choose a conversation",
      "untitled": "Untitled conversation",
      "test": "Send test message",
      "sample_title": "Values for the test",
      "sample_hint": "The message uses variables. Before a test is sent each needs a value: type one, or take them from the workflow's last run.",
      "use_last_run": "Use the last run's values",
      "last_run_filled": "Filled from the last run. A variable that run had no value for stays empty; change any value before sending.",
      "no_run": "No run yet. Type the values, or run the workflow (or Run from here) and use its values.",
      "value_of": "Value of {path}",
      "preview": "Preview of what will be sent",
      "preview_formatting": "Telegram formatting: {mode}",
      "preview_invalid": "Telegram would refuse this {mode}:",
      "formatting": "Telegram formatting",
      "formatting_plain": "Plain text",
      "formatting_html": "HTML",
      "formatting_markdown_v2": "MarkdownV2",
      "formatting_plain_hint": "Sent exactly as written: tags and markers show as they are.",
      "formatting_html_hint": "Telegram’s own tags only, like these. In the words, write <, > and & as entities, as on the last line of the example.",
      "formatting_markdown_v2_hint": "Markers like these. Outside them, put a backslash before each reserved character, as on the last line of the example.",
      "missing": "Fill these before sending: {names}",
      "status": {
        "sent": "Sent",
        "partial": "Sent to some targets only",
        "failed": "Not sent"
      },
      "sending": "Sending the test message…",
      "sent_to": "Sent to",
      "message_ids": "Message id",
      "no_answer": "The hub did not answer within {seconds} s. Nothing is known to have been sent; check the hub log for “workflow send test”.",
      "bad_answer": "The hub’s answer could not be read, so nothing is known to have been sent. Check the hub log for “workflow send test”.",
      "last_run_empty": "The last run had no value for: {names}. Type one before sending.",
      "blocked": {
        "no_target": "Choose where to send it first: Telegram and/or a conversation.",
        "no_chat": "Type the Telegram chat id first.",
        "no_conversation": "Choose the conversation first.",
        "no_text": "Write the message first."
      }
    },
    "conversation": {
      "label": "Conversation",
      "mode_new": "A new conversation for each run",
      "mode_new_hint": "Each run opens its own conversation (as before).",
      "mode_reuse": "The same conversation every run",
      "mode_reuse_hint": "Each run sends its prompt into one existing conversation, so its history stays in one place.",
      "pick": "Choose the conversation",
      "pick_placeholder": "Search this profile’s conversations",
      "count": "{count} conversations",
      "none": "No conversations in this profile yet.",
      "last_active": "Last active {when}",
      "paste_id": "Paste a conversation id instead",
      "use_list": "Choose from the list instead",
      "id": "Conversation id",
      "id_hint": "The id of a conversation in this profile, or a variable such as {{trigger.body.conversation}} filled when the step runs.",
      "template_note": "This id is a variable: it is filled when the step runs, so it cannot be tested now.",
      "not_an_id": "This is not a conversation id. Paste the whole id, or choose from the list.",
      "none_yet": "Choose a conversation, or paste its id.",
      "test": "Test conversation",
      "open": "Open conversation",
      "create_if_missing": "Create if missing",
      "create_on_hint": "If the conversation no longer exists, a run makes a new one with this step’s agent and the title below, and the step then uses it (the inbox says so).",
      "create_off_hint": "If the conversation is missing, deleted or not in this profile, the step fails and says why. No new conversation is made.",
      "new_title": "Title of a new conversation",
      "agent_hint": "The conversation keeps its own agent: the step’s agent must be the same one, or leave the agent empty to use the conversation’s. Runs that reach it at the same time take turns.",
      "status": {
        "ready": "“{title}” is ready. Its agent is {agent}, and it is free now.",
        "busy": "“{title}” exists, but a turn is going on in it. A run would wait for it to end (up to 10 minutes).",
        "not_found": "No such conversation in this profile. It was deleted, never made, or belongs to another profile.",
        "not_allowed": "“{title}” is a room seat’s or another person’s assistant. A workflow cannot talk in it.",
        "agent_mismatch": "“{title}” belongs to {agent}, not this step’s agent. Pick the same agent, or leave the step’s agent empty."
      }
    },
    "alert": {
      "title": "When a run fails",
      "inbox": "Tell me in the inbox",
      "hint": "The workflow’s name and the run’s error. Telegram and a conversation work as in a Send message step."
    },
    "test": {
      "title": "Test this step",
      "input": "Sample input ({{input}})",
      "trigger": "Sample event ({{trigger.…}}), as JSON",
      "bad_json": "The sample event is not valid JSON.",
      "execute": "Run the agent for real",
      "run": "Try it",
      "yes": "Yes — the green connections would follow",
      "no": "No — the red connections would follow"
    },
    "phase": {
      "received": "Received",
      "analyzing": "Analyzing",
      "needs_input": "Needs your answer",
      "approved": "Approved",
      "executing": "Executing",
      "completed": "Completed",
      "failed": "Failed"
    },
    "nodes": {
      "trigger": "Trigger",
      "pending": "Made when you save",
      "off": "Off",
      "add_step": "Add step",
      "add_trigger": "Add trigger",
      "add_first": "Add the first step after “{name}”",
      "add_after": "Add a step after “{name}” ({route})",
      "add_first_trigger": "Add a trigger",
      "add_first_hint": "A trigger starts the workflow: by hand, on a schedule, or when another service sends an event.",
      "trigger_link_hint": "A trigger starts the steps nothing else leads into. Connect it to one of those, or remove what leads into this step.",
      "canvas_help": "Click a step to open it. Drag a step to move it. Drag from a green dot (success) or red dot (failure) onto another step to connect them, or onto empty canvas to add a step there; the + after a step adds the next one. Keys: Tab moves between steps, Enter opens one, arrows move it, Delete removes it, + and − zoom, 0 fits everything. Ctrl+S saves.",
      "pick_step": "Add a step",
      "pick_trigger": "Add a trigger",
      "search": "Search",
      "none_found": "Nothing matches.",
      "trigger_kinds": {
        "manual": "Run by hand",
        "clickup": "ClickUp",
        "github": "GitHub",
        "generic_hmac": "Signed webhook (HMAC)",
        "token": "Webhook with a token",
        "schedule": "On a schedule"
      },
      "trigger_hints": {
        "manual": "Runs when you press Run, with the input you give it.",
        "clickup": "Runs when a ClickUp task event arrives.",
        "github": "Runs when a GitHub event arrives.",
        "generic_hmac": "Runs when a service posts a body signed with a shared secret.",
        "token": "Runs when a service posts with a secret token in a header.",
        "schedule": "Runs at set times: a cron expression, every so many minutes, or once."
      },
      "back_to_canvas": "Back to canvas",
      "preview": "What this step reads and said",
      "input": "Input",
      "output": "Output (last run)",
      "trigger_value": "What the trigger sent, when a trigger starts the run.",
      "no_output": "The last run did not reach this step.",
      "no_run": "No run yet. Run the workflow, or use “Test this step”, to see what it says.",
      "no_value": "Empty",
      "manual_about": "Every workflow can be run by hand. The input below is what its steps read as {input}.",
      "manual_detail": "Press Run",
      "remove_trigger": "Remove trigger",
      "pending_webhook": "This trigger is made when you save the workflow; its address to give the other service appears then.",
      "pending_schedule": "This schedule is made when you save the workflow.",
      "schedule_save": "Save schedule",
      "schedule_open": "Open in Schedules",
      "no_steps": "Add a step first.",
      "all_saved": "Saved",
      "save_shortcut": "Save (Ctrl+S)",
      "settings": "Workflow settings"
    },
    "leave": {
      "title": "You have unsaved changes",
      "body": "Save them before you leave this workflow, or discard them?",
      "stay": "Stay",
      "discard": "Discard",
      "save": "Save and leave",
      "left_behind": "You left this workflow with unsaved changes.",
      "restore": "Bring them back"
    }
  },
  "audit": {
    "period": "Period",
    "between": "{from} to {to}",
    "runs": "Runs",
    "runs_n": "{count} runs",
    "sessions_n": "{count} sessions",
    "tokens": "Tokens",
    "tokens_n": "{count} tokens",
    "cost": "Cost",
    "cost_unknown": "No published price",
    "model": "Model",
    "by_model": "By model",
    "period_7": "Last 7 days",
    "period_30": "Last 30 days",
    "period_90": "Last 90 days",
    "period_365": "Last 365 days"
  },
  "usage": {
    "profile": "Profile",
    "all_profiles": "All profiles",
    "agent": "Agent",
    "all_agents": "All agents",
    "removed_agent": "Removed agent",
    "show_cost": "Show cost",
    "show_cost_hint": "An estimate from published prices, not an invoice.",
    "total_tokens": "Total tokens",
    "input_output": "{input} in · {output} out",
    "input": "Input",
    "output": "Output",
    "cache_read": "Cache read",
    "cache_write": "Cache write",
    "cache_hit_rate": "Cache hit rate",
    "not_reported": "Not reported",
    "cache_not_reported_hint": "No provider reported its cache in this period.",
    "cost_estimated": "Estimated cost",
    "cost_unpriced": "Nothing in this period had a known price.",
    "conversations": "Conversations",
    "per_day": "Conversations a day (average)",
    "daily": "Tokens per day",
    "day_table": "Day by day",
    "date": "Day",
    "only_active_days": "Days without runs are left out.",
    "by_agent": "By agent",
    "no_usage_reported": "Reports no usage",
    "unreported_notice": "{count} runs reported no usage (coding agents never do): they are counted, but have no tokens or cost.",
    "nothing": "Nothing ran in this period."
  },
  "skills_usage": {
    "counting_since": "Counting started on {date}; skill use before that was never recorded.",
    "not_counting": "This hub has not started counting skill use.",
    "not_counted_acp": "Coding agents do not say which skill they load, so they are not counted.",
    "total_uses": "Total uses",
    "distinct": "Skills used",
    "top": "Most used",
    "never_used": "Never used in this period",
    "unknown": "Unknown",
    "unknown_hint": "The hub cannot see this Hermes's installed skills.",
    "none": "None",
    "daily": "Top skills per day",
    "other": "Other",
    "table": "Top skills",
    "skill": "Skill",
    "uses": "Uses",
    "share": "Share",
    "last_used": "Last used",
    "uses_n": "{count} uses",
    "never_used_list": "Enabled skills no run loaded",
    "nothing": "No skill was loaded in this period."
  },
  "notify": {
    "inbox": "Inbox",
    "filter": "Show",
    "all": "All",
    "unread": "Unread",
    "mark_all": "Mark all read",
    "none": "Nothing yet",
    "none_unread": "Everything is read",
    "none_body": "What happens in the hub appears here: a reply that finished, or an approval waiting for you.",
    "is_unread": "unread",
    "is_read": "read",
    "what_reaches_me": "What reaches me",
    "quiet": "Quiet hours",
    "quiet_hint": "A window in which no notification is pushed to your devices.",
    "quiet_from": "From",
    "quiet_to": "To",
    "push_note": "Quiet hours are about device notifications, not the inbox — what is written at night is still here in the morning.",
    "kind": {
      "run_completed": "An agent finished",
      "approval_requested": "An approval is waiting",
      "room_mention": "You were mentioned",
      "task_moved": "A task moved",
      "schedule_failed": "A schedule failed",
      "update_available": "An update is available",
      "system": "System messages"
    },
    "test": "Send a test notification",
    "test_sent": "Sent. Check this browser and your devices.",
    "browser": {
      "title": "Notifications in this browser",
      "enable": "Enable browser notifications",
      "disable": "Turn off in this browser",
      "on": "On: what reaches your inbox also appears on this browser, even when the page is closed.",
      "off": "Off: notices appear here only while the page is open.",
      "denied": "The browser blocks notifications for this site. Allow them in the browser's site settings, then try again.",
      "unsupported": "This browser cannot receive push notifications.",
      "no_key": "Web Push is switched off on this hub. An admin can switch it on."
    },
    "col_event": "Event",
    "col_in_hub": "In the hub",
    "col_on_devices": "On devices"
  },
  "webhooks": {
    "intro": "Addresses this hub calls on its own, signed so the receiver can check the sender. Webhooks belong to the profile you are in.",
    "add": "Add webhook",
    "forwarding_note": "The hub sends each event you choose as it happens, signed, from the profiles you choose. A delivery that fails is tried again after 30 s, 1 min, 2 min and so on, then marked as given up; you can send it again from the deliveries.",
    "none": "No webhooks yet",
    "none_body": "Add one to give an outside service an address the hub can call.",
    "facts": "About this webhook",
    "enabled": "Enabled",
    "on": "On",
    "off": "Off",
    "signed": "Signed",
    "unsigned": "Not signed",
    "no_events": "No events chosen",
    "event_count": "Events: {count}",
    "private_allowed": "Private address allowed",
    "stats": "Delivered {delivered} · failed {failed}",
    "last_delivery": "last delivered {when}",
    "last_error": "Last failure: {error}",
    "test": "Send test",
    "testing": "Sending the test delivery…",
    "test_ok": "Delivered — the endpoint answered {status}.",
    "test_status": "Not delivered — the endpoint answered {status}.",
    "test_failed": "Not delivered: {error}",
    "show_deliveries": "Recent deliveries",
    "hide_deliveries": "Hide deliveries",
    "deliveries": "Recent deliveries",
    "no_deliveries": "Nothing has been sent to this webhook yet.",
    "col_when": "When",
    "col_event": "Event",
    "col_status": "Status",
    "col_code": "Response",
    "col_error": "Error",
    "delivery": {
      "queued": "Queued",
      "delivered": "Delivered",
      "failed": "Failed",
      "dead": "Gave up"
    },
    "delete_title": "Delete the webhook “{name}”?",
    "delete_body": "The hub stops calling this address and forgets its delivery history.",
    "edit_title": "Edit “{name}”",
    "name": "Name",
    "url": "Address",
    "url_hint": "An http or https URL. The hub checks where it points before saving it.",
    "url_scheme": "Use an address that starts with http:// or https://.",
    "url_unresolvable": "The hub could not find this address. Check the host name.",
    "url_private": "This address points inside a private network. Allow private addresses below if that is on purpose.",
    "allow_private": "Allow a private address",
    "allow_private_hint": "Off by default, so a webhook cannot make the hub call machines inside its own network.",
    "signing": "Signing secret",
    "signing_hint": "With a secret, every delivery carries the header below, an HMAC-SHA256 of the body, so the receiver can check it came from this hub.",
    "secret_keep": "Keep the current secret",
    "secret_new": "Make a new secret",
    "secret_new_hint": "Shown once after saving; the receiver must be given the new one.",
    "secret_remove": "Stop signing",
    "secret_make": "Sign deliveries with a new secret",
    "secret_none": "Do not sign",
    "events": "Events",
    "events_hint": "Which of the hub's events this webhook receives.",
    "events_filter": "Filter events",
    "events_no_match": "No event matches.",
    "secret_title": "Signing secret",
    "secret_once": "Copy it now. The hub keeps it but will never show it again.",
    "secret_done": "I copied it",
    "copy": "Copy",
    "copied": "Copied",
    "all_profiles": "All profiles",
    "profile_count": "Profiles: {count}",
    "with_content": "Includes message text",
    "profiles": "Profiles",
    "profiles_hint": "Events from which profiles this webhook receives. Only profiles you can enter are listed.",
    "profiles_all": "Every profile I can enter",
    "profiles_some": "Only these profiles",
    "profiles_pick_one": "Choose at least one profile.",
    "include_content": "Include message text",
    "include_content_hint": "Off: deliveries carry ids, states and times only, not what people and agents wrote.",
    "max_retries": "Retries after a failure",
    "max_retries_hint": "From 0 to 10. Each wait is twice the one before, starting at 30 seconds.",
    "col_attempts": "Attempts",
    "col_next": "Next try",
    "col_actions": "Actions",
    "redeliver": "Redeliver",
    "redelivered": "Queued again. It is sent in a moment."
  },
  "privacy": {
    "access": "Apps and devices with access",
    "access_hint": "Paired phones and integrations each hold a token that acts as you. Revoke one and it stops working at once; a paired device is unlinked.",
    "col_name": "Name",
    "col_scopes": "Can",
    "col_used": "Last used",
    "col_expires": "Expires",
    "kind_device": "Paired device",
    "kind_app": "App token",
    "never_used": "Never",
    "no_expiry": "Never",
    "revoke": "Revoke",
    "revoke_title": "Revoke “{name}”?",
    "revoke_body": "Anything using this token loses access at once. This cannot be undone.",
    "revoke_device_body": "This device loses access at once and is unlinked; pairing it again gives it a new token.",
    "none": "No app or device holds a token",
    "none_body": "Only your own sign-ins can reach your account.",
    "browsers_note": "Browsers you signed in to are not listed here. Changing your password under Account signs out every browser except this one; it does not revoke the tokens above.",
    "redact_title": "What reaches the model from messaging channels",
    "redact_admin_only": "An admin changes it."
  },
  "people": {
    "title": "People",
    "add": "Add person",
    "person": "Person",
    "role": "Role",
    "status": "Status",
    "status_active": "Active",
    "status_disabled": "Disabled",
    "workspaces": "Profiles",
    "all_workspaces": "Every profile",
    "no_workspace": "No profile",
    "make_member_for": "Make {name} a member",
    "owner_note": "The owner account is not edited here",
    "make_admin": "Make admin",
    "make_member": "Make member",
    "set_password": "Set a password",
    "set_password_for": "Password for {name}",
    "password_note": "Typed once and never shown again. It ends this person's sessions on every device.",
    "new_password": "New password",
    "password_short": "Eight characters at least",
    "disable": "Disable",
    "enable": "Enable",
    "delete_title": "Delete {name}?",
    "delete_body": "The account, its tokens and its paired devices go. Conversations stay.",
    "username": "Username",
    "username_hint": "Lowercase letters, digits, and . _ -",
    "username_bad": "Two to forty lowercase letters, digits, and . _ -",
    "display_name": "Display name",
    "member_can": "Opens chats and agents in their profiles",
    "admin_can": "Manages people, profiles and hub settings",
    "lockouts": "Lockouts",
    "clear_lockouts": "Clear all",
    "no_lockouts": "No lockouts",
    "no_lockouts_body": "An address locks after repeated failed sign-ins and unlocks itself when the time is up.",
    "ip": "Address",
    "lock_kind": "Reason",
    "lock_password": "Password",
    "lock_token": "Token",
    "lock_pairing": "Pairing",
    "failures": "Attempts",
    "locked_until": "Until",
    "edit_workspaces": "Profiles…",
    "workspaces_for": "Profiles of {name}",
    "workspaces_hint": "A member sees only the profiles chosen here: their chats, agents and tasks.",
    "workspaces_required": "Choose at least one profile.",
    "admin_everywhere": "An admin enters every profile."
  },
  "workspaces": {
    "note": "Separate profiles: their agents, models and conversations do not see each other.",
    "add": "New profile",
    "current": "Current",
    "default": "Default",
    "counts": "{agents} agents · {sessions} conversations",
    "archive": "Archive",
    "archive_title": "Archive {name}?",
    "archive_body": "The profile is folded away here and its memberships end. Its Hermes profile and its conversations ({sessions}) stay and are not erased.",
    "name": "Name",
    "slug": "Slug",
    "slug_hint": "Lowercase letters, digits and dashes — it appears in links.",
    "slug_bad": "Lowercase letters, digits and dashes, starting and ending with one",
    "slug_taken": "That slug is taken",
    "clone": "Copy from",
    "clone_what": "Copies the config, the personality (SOUL.md) and the skills — not memory, chats or channels.",
    "origin": "How does it start?",
    "origin_blank": "From scratch",
    "origin_clone": "Copy of a profile",
    "origin_blank_what": "A fresh Hermes profile with its default settings and bundled skills.",
    "clone_pick": "Choose the profile",
    "refused": "Hermes refused: {message}",
    "rename_title": "Rename {name}",
    "rename_hint": "Any name, in any language. It is shown wherever this profile appears, and Hermes shows it too. The id {slug} does not change, so chats, channels and schedules keep working.",
    "export": "Export",
    "export_title": "Export {name}",
    "export_what": "Hermes writes this profile as its own archive (.tar.gz): its personality (SOUL.md), memory, skills, settings and chats. The file can be imported here, into Ekko, or with hermes profile import.",
    "export_secrets": "Keys stay behind: credential files (.env, auth.json) are left out, and any provider key stored in this hub is blanked wherever it appears. Only you can download the archive, for 24 hours.",
    "export_ready": "{file} ({size}) is ready and was saved.",
    "export_download": "Download again",
    "export_removed": "Left out: {files}",
    "export_masked": "Stored keys blanked in: {files}",
    "import": "Import",
    "import_title": "Import a profile",
    "import_what": "Choose a Hermes profile archive (.tar.gz) — one exported here, from Ekko, or with hermes profile export. Hermes makes a new profile from it under the slug below.",
    "import_choose": "Choose an archive",
    "import_other_file": "Choose another",
    "import_no_file": "No file chosen",
    "import_uploading": "Uploading the archive…",
    "import_too_large": "The archive is larger than the hub accepts for an upload (50 MB).",
    "import_done": "{name} was imported.",
    "import_replace_default": "Replace the default profile with this one",
    "import_replace_default_hint": "For moving here from another program: the archive becomes the default profile, and the current default is kept as a backup you can switch back to.",
    "import_replace_name_hint": "The default profile takes this name.",
    "import_replace_confirm_title": "Replace the default profile?",
    "import_replace_confirm_body": "Your current default profile “{name}” will be replaced by this archive, and the archive becomes the default. The current one is kept whole as a new profile named default-backup (or the next free name): its memory, skills, Hermes conversations, schedules and channels go with it, and its channels keep answering from there. A conversation running right now stops. Shared providers and their keys stay with the default. If anything fails, nothing changes.",
    "import_replace_confirm": "Replace the default",
    "import_replace_done": "Your previous default is kept as “{backup}”; you can delete it any time.",
    "import_replace_open": "Switch to {backup}",
    "export_providers": "Providers",
    "export_without_providers": "Without providers",
    "export_with_providers": "With providers",
    "export_keys_warning": "The file will contain this profile's providers with their API keys in the clear — its own and the shared ones it uses. Anyone who has the file can use those keys. Importing it makes them the new profile's own.",
    "export_providers_carried": "Providers in the file, keys included: {count}"
  },
  "knowledge": {
    "kind": "Kind",
    "kind_all": "All",
    "kind_journal": "Journal",
    "kind_note": "Notes",
    "kind_file": "Files",
    "search": "Search knowledge",
    "untitled": "Untitled",
    "more": "There is more — narrow the search or the kind.",
    "empty": "Nothing yet",
    "empty_body": "Journal entries, notes and files appear here as they are written or attached in a conversation.",
    "no_match": "No matches",
    "no_match_body": "Change the word, or widen the kind."
  },
  "plugins": {
    "none": "No plugins",
    "none_body": "This hub answers the list and has no installer yet. When one exists, what is installed shows here with its state.",
    "name": "Plugin",
    "kind": "Kind",
    "status": "Status",
    "status_running": "Running",
    "status_stopped": "Stopped",
    "status_error": "Error"
  },
  "updates": {
    "web_note": "This page is about the phone and desktop apps. The web you are looking at is served by the hub and changes when it does.",
    "source": "Source and channel",
    "default_channel": "Default channel",
    "channel_stable": "Stable",
    "channel_test": "Test",
    "from_source": "Fetch releases from a repository",
    "from_source_hint": "The hub reads GitHub releases itself instead of you uploading them.",
    "repo": "Repository",
    "repo_hint": "As owner/repo.",
    "token": "Token",
    "token_hint": "For a private repository. Written once, never shown.",
    "token_stored": "Stored. Type a new one to replace it.",
    "auto_publish": "Publish to the test channel automatically",
    "auto_publish_hint": "Every new release at the source is published to test on its own.",
    "shelf": "Releases",
    "empty": "No releases",
    "empty_body": "What is published here is what the phone and desktop apps find. None of them is built yet.",
    "build": "Build",
    "size": "Size",
    "published": "Published",
    "mandatory": "Mandatory",
    "platform_android": "Android",
    "platform_ios": "iOS",
    "platform_macos": "macOS",
    "platform_windows": "Windows",
    "platform_linux": "Linux",
    "delete_title": "Remove {version} from the shelf?",
    "delete_body": "It disappears from the update list. A build already downloaded keeps working."
  },
  "about": {
    "name": "Name",
    "server_version": "Server build",
    "contract_version": "Contract version",
    "client_version": "This page was built from",
    "locales": "Languages",
    "realtime": "Live channels",
    "realtime_note": "What a client opens so it learns what happens without asking."
  },
  "skills": {
    "title_of": "{name} skills",
    "search": "Search skills",
    "new": "New skill",
    "none": "No skills",
    "none_body": "Skills are files in the agent's folder. Add one here, or install a pack.",
    "no_match": "No matches",
    "no_match_body": "Change the word.",
    "category_user": "Your skills",
    "enabled": "Enabled",
    "pinned": "Pinned",
    "pin": "Pin",
    "unpin": "Unpin",
    "broken": "Unreadable",
    "delete_title": "Delete {name}?",
    "delete_body": "The whole skill folder goes, with every file in it. Turning it off keeps everything.",
    "key": "Key",
    "document": "SKILL.md",
    "editor_note": "This is what the agent reads, verbatim — front matter and body. Nothing is reshaped on save.",
    "import": {
      "button": "Import",
      "running": "Importing…",
      "done": "Imported {count}: {names}.",
      "reason": {
        "pack_unrecognised": "A skill pack is a SKILL.md file or a zip archive",
        "pack_has_no_skill": "The archive holds no SKILL.md",
        "pack_path_unsafe": "The archive has a path that would land outside its skill",
        "pack_unsupported": "The archive uses something the hub does not read",
        "pack_corrupt": "The archive is damaged",
        "pack_too_large": "The archive is too large once unpacked",
        "skill_empty": "SKILL.md is empty",
        "skill_too_large": "SKILL.md is longer than Hermes reads",
        "skill_front_matter_missing": "SKILL.md does not start with a front matter block (---)",
        "skill_front_matter_unclosed": "SKILL.md's front matter is not closed with ---",
        "skill_front_matter_invalid": "SKILL.md's front matter is not valid YAML",
        "skill_name_required": "SKILL.md's front matter has no name",
        "skill_description_required": "SKILL.md's front matter has no description",
        "skill_description_too_long": "The description is longer than Hermes reads",
        "skill_body_empty": "SKILL.md has nothing after its front matter",
        "skill_name_invalid": "The skill name is not one Hermes accepts",
        "skill_not_utf8": "SKILL.md is not UTF-8 text",
        "skill_nested": "A skill in the archive sits inside another skill",
        "skill_duplicate": "The same skill ({skill}) is in the upload twice",
        "skill_exists": "This profile already has the skill {skill}; delete it first to replace it"
      }
    },
    "builtin": "Built into Hermes",
    "builtin_hint": "One of Hermes's own skills. Hermes keeps it in step with its bundle, so it can be read, pinned and switched on or off here (Hermes's own switch) but not edited or deleted.",
    "bundled_refused": "This is one of Hermes's own skills; it is read-only here.",
    "close": "Close",
    "library": {
      "title": "Core Hub library",
      "hint": "One of Core Hub's own skills. Core Hub keeps it up to date while it is as shipped; edit it freely — an edited skill is yours and is never overwritten.",
      "edited": "Edited",
      "edited_hint": "You changed this skill, so Core Hub no longer updates it. Restore puts the library's version back.",
      "edited_count": "{count} edited",
      "restore": "Restore",
      "restore_title": "Restore {name}?",
      "restore_body": "Your changes to this skill are replaced by the version Core Hub ships. A skill you switched off stays off.",
      "restored": "{name} is back to the library's version.",
      "summary_on": "{installed} of {available} skills in this profile — images, documents, data, research, translation, slides and schedules. Kept up to date until you edit one.",
      "summary_none": "Not installed in this profile yet: {available} skills — images, documents, data, research, translation, slides and schedules.",
      "summary_off": "Off in this profile. Switch it on to install Core Hub's skills again.",
      "install": "Install",
      "switch": "Core Hub library in this profile",
      "off_title": "Switch the Core Hub library off?",
      "off_body": "Its skills are removed from this profile, except the ones you edited, which stay as your own. Core Hub stops installing and updating them here until you switch it back on.",
      "off_confirm": "Switch off",
      "editor_note": "One of Core Hub's own skills. Once you save a change it is yours: Core Hub stops updating it, and Restore brings its version back.",
      "not_library": "This skill is not one of Core Hub's own.",
      "off_refused": "The Core Hub library is off in this profile; switch it on first."
    },
    "essential_refused": "Hermes's own manual (hermes-agent) is always on: Hermes does not let it be switched off."
  },
  "mcp": {
    "title_of": "{name} MCP servers",
    "new": "New server",
    "enabled": "Enabled",
    "name": "Name",
    "config": "Server config",
    "name_bad": "Letters, digits and . _ - only",
    "invalid_json": "Not valid JSON yet.",
    "none": "No servers",
    "none_body": "MCP servers give the agent tools beyond its own. Add one here.",
    "restart_note": "This page writes to the agent's config file. Hermes connects to these servers when it starts, so restart it for a change to take effect.",
    "agent_note": "This page writes {name}'s own MCP servers in {path}, the same in every profile. {name} reads them when a conversation starts, so the next conversation has a change. A server switched off is kept by Core Hub, out of that file, until you switch it on.",
    "agent_note_no_path": "This page writes {name}'s own MCP servers, the same in every profile. {name} reads them when a conversation starts, so the next conversation has a change.",
    "not_managed": "Core Hub does not list {name}'s own MCP servers yet. They are set in its settings file, which you can edit under Config files. The Core Hub tools below still reach {name}.",
    "config_has_comments": "The agent's settings file has comments, which Core Hub would lose by rewriting it. Remove them under Config files, or change the server there.",
    "config_busy": "The agent is writing its settings file right now. Try again in a moment.",
    "editor_note": "Every MCP server has its own fields. It is edited as it is, and nothing we do not recognise is dropped. A credential reads as [stored]; leaving it keeps the one on file.",
    "delete_title": "Delete {name}?",
    "delete_body": "It goes from the config file. Nothing is removed from the server itself.",
    "address": "Address",
    "command": "Command",
    "test": {
      "button": "Test",
      "running": "Testing…",
      "ok": "Hermes connected — {count} tools, {seconds} s.",
      "no_tools": "The server lists no tools.",
      "failed": "Hermes could not connect:"
    },
    "oauth": {
      "status": {
        "connected": "Connected",
        "expired": "Expired",
        "not_connected": "Not connected",
        "error": "Sign-in unreadable"
      },
      "connect": "Connect OAuth",
      "reconnect": "Reconnect OAuth",
      "disconnect": "Disconnect",
      "disconnect_title": "Disconnect {name}?",
      "disconnect_body": "Hermes forgets the sign-in to this server in this profile. Other profiles keep theirs. A conversation already using the server keeps it until the agent restarts.",
      "per_profile": "Each profile connects separately.",
      "starting": "Asking Hermes to start the sign-in…",
      "waiting": "Finish signing in on the page that opened in a new tab; this page updates by itself.",
      "open_page": "Open the sign-in page",
      "approved": "Connected — Hermes lists {count} tools.",
      "test_now": "Test now",
      "failed": "The sign-in did not finish:",
      "cancelled": "The sign-in was cancelled.",
      "expired": "The sign-in ran out before it finished. Start it again.",
      "test_needs": "This server needs a sign-in.",
      "starting_short": "Starting…"
    },
    "signin_add": {
      "how": "How to add it",
      "mode_json": "JSON",
      "mode_signin": "Sign in (OAuth)",
      "note": "For a server that signs in with its provider, such as ClickUp: give its address, sign in, and Core Hub writes the rest.",
      "about": "Core Hub adds the server with this address, opens the provider's sign-in page in a new tab, and tests the server once you are signed in.",
      "url": "Server address",
      "url_bad": "An https:// address (http:// only for this computer).",
      "submit": "Add and sign in",
      "name_taken": "A server named {name} already exists; choose another name."
    },
    "tools": {
      "count": "{count} tools",
      "count_filtered": "{allowed} of {total} tools",
      "failed_badge": "Test failed",
      "tested_at": "Tested {when}",
      "stale": "This server's settings changed after the last test, so the list may be out of date. Test it again.",
      "auto_testing": "Testing the server to list its tools…",
      "heading": "Tools the agent may use",
      "allowed": "{allowed} of {total} allowed",
      "all": "All",
      "none": "None",
      "read_only": "Read-only",
      "read_only_hint": "Read-only ticks the tools that only read, list, search or get, and unticks those that create, change, delete, send or run. Each tool says how it was read; check before saving.",
      "access": {
        "read": "Reads",
        "write": "Changes",
        "unknown": "Unclear"
      },
      "access_annotation": "The server marks this tool read-only.",
      "access_name": "Read from the tool's name.",
      "search": "Search tools",
      "no_match": "No tool matches.",
      "locked": "Set by the pattern {pattern} in the config file.",
      "note_all": "No filter: the agent gets every tool, including any the server adds later.",
      "note_include": "Only the ticked tools. A tool the server adds later stays off until you tick it.",
      "note_exclude": "Every tool except the unticked ones, including any the server adds later.",
      "undo": "Undo",
      "saved": "Saved. Hermes applies it the next time it connects to this server, after a restart."
    }
  },
  "hub_tools": {
    "title": "{product} tools",
    "subtitle": "{product} offers itself to this agent as an MCP server, so the agent can work with the board, schedules, conversations and files of this profile.",
    "enabled": "Offer {product} tools",
    "acts_as": "Every call acts for the person whose conversation it is, in this profile only, with that person's permissions — never as an admin. Only the hub's own conversations can use them; a message arriving from a channel cannot.",
    "shared_note": "These settings are the profile's, the same on every agent's page. {name} is handed {product}'s server when a conversation starts, if it can reach an MCP server over HTTP.",
    "allow_writes": "Allow changes",
    "writes_only": "Only changes: nothing here reads.",
    "recent": "Recent calls",
    "no_calls": "No calls yet.",
    "call_ok": "done",
    "call_failed": "refused",
    "group": {
      "tasks": "Tasks",
      "schedules": "Schedules",
      "conversations": "Conversations",
      "notifications": "Notifications",
      "workflows": "Workflows",
      "files": "Files",
      "devices": "Your computers"
    },
    "group_about": {
      "tasks": "List the board's tasks; with changes allowed, create, move, assign and comment on them.",
      "schedules": "List schedules; with changes allowed, create one, pause it or run it now.",
      "conversations": "List and search this profile's conversations and read a summary of one.",
      "notifications": "Tell the person something: a notice in their inbox.",
      "workflows": "List workflows; with changes allowed, run one.",
      "files": "Read the files in this profile's folder; with changes allowed, write them.",
      "devices": "Use the shared folders and the programs on the person's own computers that run the Core Hub app (each computer asks its person first). With changes allowed, write files and run programs there."
    },
    "unavailable": {
      "runtime_absent": "There is no Hermes beside this hub to give the tools to.",
      "hermes_profile_absent": "Hermes has no folder for this profile yet. Start a conversation in it first."
    },
    "reason": {
      "hub_tools_no_live_run": "no conversation was running, so there was nobody to act for",
      "hub_tools_run_ambiguous": "several people had conversations running and the hub could not tell whose it was",
      "hub_tools_tool_off": "that tool is switched off here",
      "path_outside_profile": "the path was outside the profile's folder",
      "forbidden": "the person may not do that",
      "not_found": "not found",
      "validation_failed": "the arguments were not valid",
      "hub_tools_sender_not_linked": "the message came from a messaging account nobody linked",
      "hub_tools_sender_no_access": "the person who linked that account may not use this profile",
      "hub_tools_group_chat": "the message came from a group chat"
    }
  },
  "memory": {
    "title_of": "{name} memory",
    "persona": "read at every start",
    "content": "Text",
    "note": "Three documents the agent reads when a conversation starts, so a change here reaches its next conversation. They are emptied by editing, never deleted.",
    "write": "Write",
    "doc_soul": "Persona",
    "doc_memory": "Memory",
    "doc_user": "About you",
    "about_soul": "Who the agent is. Read at the start of every conversation.",
    "about_memory": "What it learned and chose to keep.",
    "about_user": "What it knows about you.",
    "empty_soul": "Not written yet — the agent has no particular character.",
    "empty_memory": "It has kept nothing yet.",
    "empty_user": "It has written nothing about you yet.",
    "entries_hint": "A list of short entries, each separated by a line holding only §. It has a character limit: past it, the agent cannot add anything new.",
    "too_long": "Too long for this memory: {length} characters, and the limit is {limit}. Shorten or merge entries.",
    "add_entry": "Add entry",
    "edit_all": "Edit all",
    "edit_entry": "Edit this entry",
    "remove": "Remove",
    "remove_title": "Remove this entry?",
    "entry": "Entry",
    "add_entry_title": "New entry in {name}",
    "edit_entry_title": "An entry in {name}",
    "budget": "{count} of {limit} characters",
    "budget_over": "{count} of {limit} characters — full: the agent cannot keep anything new"
  },
  "channels": {
    "title_of": "{name} channels",
    "enabled": "Enabled",
    "exclusive": "one identity",
    "not_configured": "not configured",
    "fields_n": "{count} fields",
    "clear": "Forget identity",
    "none": "No platform linked yet",
    "none_body": "Link a platform and the agent answers people there: Telegram, WhatsApp, Discord, email and more.",
    "restart_note": "Hermes connects to these platforms when it starts: restart it for a change to take effect.",
    "editor_note": "The fields come from the agent's file, not from a form we wrote. A secret reads as [stored]; leaving it keeps the one on file.",
    "secret_hint": "Leave it to keep the one already stored.",
    "no_fields": "This channel has no fields yet.",
    "clear_title": "Forget the {name} identity?",
    "clear_body": "The tokens and secrets are removed and the channel is switched off. Its other settings stay.",
    "login": {
      "button": "Pair by QR",
      "title": "Link {name}",
      "note": "Hermes pairs this profile's agent with a phone. Scan the code from WhatsApp on the phone: Settings → Linked devices → Link a device.",
      "expires": "The code is valid until {time}.",
      "done": "Linked. The agent answers on it after its next restart.",
      "done_as": "Linked as {account}. The agent answers on it after its next restart.",
      "failed": "The pairing failed.",
      "cancelled": "The pairing was stopped.",
      "again": "Start again",
      "close": "Close",
      "not_supported": "This platform does not pair by QR; fill in its fields instead.",
      "done_now": "Linked. The agent answers on it now; the first person to message it waits for your approval here.",
      "done_as_now": "Linked as {account}. The agent answers on it now; the first person to message it waits for your approval here.",
      "done_self_now": "Linked. Open “Message yourself” in WhatsApp on your phone and write to the agent.",
      "done_as_self_now": "Linked as {account}. Open “Message yourself” in WhatsApp on your phone and write to the agent."
    },
    "linked": "linked",
    "not_linked": "not linked",
    "linked_as": "Linked as {account}",
    "unlink": "Unlink",
    "unlink_title": "Unlink {name}?",
    "unlink_body": "The agent stops answering this number, its session is deleted from this profile and the channel is switched off. Approved senders stay. Remove the device on the phone too: WhatsApp → Linked devices.",
    "unlinked_note": "Unlinked. Remove the device from WhatsApp on the phone too: Settings → Linked devices.",
    "unlink_not_linked": "No number is linked in this profile.",
    "unlink_not_supported": "This platform is not linked to a device, so there is nothing to unlink; forget its identity instead.",
    "applies_now": "In this profile the hub restarts the messaging gateway itself, so a change takes effect at once.",
    "status": {
      "online": "online",
      "offline": "offline",
      "error": "error",
      "unknown": "unknown"
    },
    "gateway": {
      "running": "gateway running",
      "starting": "gateway starting",
      "stopped": "no gateway running",
      "error": "gateway failing"
    },
    "how": {
      "title": "How to use it",
      "step_message": "1) Message the linked number from another WhatsApp account.",
      "step_message_as": "1) Message {account} from another WhatsApp account.",
      "step_approve": "2) The sender receives a pairing code and the request appears under “Approvals” at the top of this page: approve it and the agent starts answering them.",
      "personal_warning": "Warning: if this is your personal number, everyone who messages it receives the agent’s pairing reply. Choose “Me (Message yourself)” instead.",
      "self_step_open": "1) On your phone, open WhatsApp and the “Message yourself” chat (search for your own name).",
      "self_step_open_as": "1) On the phone with {account}, open WhatsApp and the “Message yourself” chat (search for your own name).",
      "self_step_write": "2) Write to the agent there. It answers in the same chat, each reply marked as the agent’s.",
      "self_note": "Nobody else who messages this number reaches the agent."
    },
    "pairing": {
      "title": "Waiting for approval",
      "note": "Someone who messages the agent for the first time receives a pairing code and waits here. A request expires after an hour.",
      "none": "No requests right now.",
      "unnamed": "no name",
      "approve": "Approve",
      "deny": "Deny",
      "approved_title": "Approved senders",
      "approved_none": "Nobody is approved yet.",
      "revoke": "Remove",
      "revoke_title": "Remove {name}?",
      "revoke_body": "The agent will not answer them any more, and their next message becomes a new pairing request.",
      "just_now": "just now",
      "minutes_ago": "{count} min ago",
      "hours_ago": "{count} h ago"
    },
    "telegram": {
      "link_button": "Link",
      "title": "Link Telegram",
      "intro": "The agent answers on a Telegram bot of your own. You make the bot in Telegram in a minute; the hub never makes one for you.",
      "step_open": "Open Telegram and search for @BotFather (the official one, with the blue tick).",
      "step_newbot": "Send /newbot, then choose a name and a username ending in “bot”.",
      "step_copy": "BotFather replies with a token like 123456789:AA… — copy it and paste it below.",
      "token": "Bot token",
      "token_hint": "Kept as a secret in this profile's Hermes files and never shown again.",
      "allowed": "People who skip approval (optional)",
      "allowed_hint": "Telegram user IDs, separated by commas. To find yours, message @userinfobot in Telegram. Everyone else gets a pairing code to approve here.",
      "allowed_invalid": "A Telegram user ID is digits only.",
      "submit": "Check and link",
      "checking": "Asking Telegram…",
      "done_as": "Linked: {account}.",
      "done_now": "The agent answers on the bot now. The first person to message it waits for your approval under “Approvals” at the top of the Channels page.",
      "done_restart": "In the default profile, restart Hermes from its card so it starts answering on the bot.",
      "token_invalid": "That does not look like a bot token. It is the bot's number, a colon, and a long code, as BotFather sent it.",
      "token_rejected": "Telegram refused this token: {message}. Copy it again from BotFather (or /token there for a new one).",
      "unreachable": "Telegram did not answer. Check the hub's internet connection and try again.",
      "token_in_use": "This bot is already linked in the profile “{profile}”. A bot answers in one place only: unlink it there first.",
      "how_title": "How to start",
      "how_open": "1) Open the bot:",
      "how_open_unnamed": "1) Open the bot in Telegram.",
      "how_message": "2) Send it any message. It replies with a pairing code.",
      "how_approve": "3) Approve the request under “Approvals” at the top of this page; from then on the agent answers.",
      "unlink_body": "The token is removed from this profile and the agent stops answering on the bot. The bot itself stays in Telegram (delete it with @BotFather if you want). The people you allowed stay listed.",
      "unlinked_note": "Unlinked. The bot no longer answers from this profile."
    },
    "settings": {
      "open": "Settings",
      "title": "Telegram settings",
      "applies_now": "Saving restarts this profile's gateway, so changes apply at once.",
      "applies_restart": "In the default profile, changes apply after Hermes restarts.",
      "section": {
        "access": "Who may message the bot",
        "replies": "Replies",
        "groups": "Groups and channels",
        "media": "Media & voice",
        "advanced": "Advanced"
      },
      "media_note": "These are the profile's own settings: they change WhatsApp and every other channel of this profile too.",
      "shared": "Also changes WhatsApp and other channels",
      "default_is": "Default: {value}",
      "default_none": "Default: none",
      "on": "on",
      "off": "off",
      "reset": "Back to default",
      "discard": "Discard changes",
      "list_placeholder": "123456789, 987654321",
      "invalid": "This value is not accepted. Check the format.",
      "saved_now": "Saved. The gateway restarts with the new settings.",
      "saved_restart": "Saved. Restart Hermes from its card for them to apply.",
      "option": {
        "allowed_users": {
          "label": "People who skip approval",
          "help": "Telegram user IDs that may message the bot without a pairing code. Find yours by messaging @userinfobot."
        },
        "unauthorized_dm_behavior": {
          "label": "When a stranger messages the bot",
          "help": "Send them a pairing code to approve here, or ignore them silently."
        },
        "show_reasoning": {
          "label": "Show the model's thinking",
          "help": "Before each answer, add the model's reasoning as a separate block."
        },
        "reasoning_style": {
          "label": "How the thinking looks",
          "help": "The format of the thinking block."
        },
        "tool_progress": {
          "label": "Show what the agent is doing",
          "help": "Messages about the tools the agent uses while it works."
        },
        "cleanup_progress": {
          "label": "Remove progress messages when done",
          "help": "Delete the “working…” and tool messages after a successful answer."
        },
        "streaming": {
          "label": "Write the answer as it comes",
          "help": "Show the answer while it is being written, by editing one message, instead of sending it at the end."
        },
        "reply_to_mode": {
          "label": "Reply as a quote",
          "help": "Whether answers quote the message they answer."
        },
        "reactions": {
          "label": "Reactions",
          "help": "React with 👀 while working and ✅ or ❌ when done."
        },
        "typing_indicator": {
          "label": "“Typing…” indicator",
          "help": "Show “typing…” while the agent works."
        },
        "disable_link_previews": {
          "label": "No link previews",
          "help": "Send links without Telegram's preview card."
        },
        "notifications": {
          "label": "Notification sound",
          "help": "Which bot messages make the phone ring."
        },
        "require_mention": {
          "label": "Answer in groups only when mentioned",
          "help": "In a group, the bot answers only messages that @mention it or reply to it. Private chats are not affected."
        },
        "allowed_chats": {
          "label": "Only these groups",
          "help": "Group chat IDs the bot answers in (IDs of groups start with -100). Empty: any group it is added to."
        },
        "free_response_chats": {
          "label": "Groups where no mention is needed",
          "help": "Group chat IDs where the bot answers every message even when mentions are required."
        },
        "group_allowed_users": {
          "label": "People allowed in groups",
          "help": "Telegram user IDs whose group messages the bot answers. This does not let them message it privately."
        },
        "observe_unmentioned_group_messages": {
          "label": "Read group messages it does not answer",
          "help": "Keep unmentioned group messages as context for the next answer."
        },
        "guest_mode": {
          "label": "Answer a mention in any group",
          "help": "Answer an @mention even in groups not in the list above."
        },
        "exclusive_bot_mentions": {
          "label": "Ignore messages for other bots",
          "help": "Skip a group message that mentions another bot."
        },
        "allow_bots": {
          "label": "Messages from other bots",
          "help": "Whether messages sent by other bots are answered."
        },
        "stt_enabled": {
          "label": "Transcribe voice notes",
          "help": "Voice notes people send are turned into text for the agent."
        },
        "stt_echo_transcripts": {
          "label": "Show the transcript",
          "help": "Send the transcribed text back so the person sees what was understood."
        },
        "voice_auto_tts": {
          "label": "Answer with voice notes",
          "help": "Answers are also sent as voice. In a chat, /voice changes this for that chat."
        },
        "home_channel": {
          "label": "Home chat",
          "help": "The chat ID where scheduled tasks and notices are delivered. In Telegram, /sethome in a chat sets it too."
        },
        "gateway_restart_notification": {
          "label": "Say when back online",
          "help": "Send a message to the home chat when the agent restarts."
        },
        "command_menu_max": {
          "label": "Commands in the menu",
          "help": "How many commands the bot's “/” menu lists (1–100)."
        },
        "proxy_url": {
          "label": "Proxy",
          "help": "Reach Telegram through a proxy, e.g. socks5://host:1080. Leave empty for a direct connection."
        },
        "allowed_roles": {
          "label": "Allowed roles",
          "help": "Server role ids (digits). The bot answers anyone who has one."
        },
        "allowed_channels": {
          "label": "Only these channels",
          "help": "Ids of the channels it answers in. Empty: any channel it can see."
        },
        "free_response_channels": {
          "label": "Channels with no mention needed",
          "help": "Ids of channels where it answers every message."
        },
        "ignored_channels": {
          "label": "Ignored channels",
          "help": "Ids of channels it never answers in, even when mentioned."
        },
        "auto_thread": {
          "label": "Answer in a thread",
          "help": "When mentioned in a channel it opens a thread and answers there."
        },
        "thread_require_mention": {
          "label": "In threads: answer only when mentioned",
          "help": "Inside threads it answers only when mentioned."
        },
        "no_thread_channels": {
          "label": "Channels without threads",
          "help": "Ids of channels where it answers directly, without a thread."
        },
        "disable_dms": {
          "label": "Ignore direct messages",
          "help": "It does not answer DMs, only channels."
        },
        "reply_in_thread": {
          "label": "Answer in a thread",
          "help": "It answers in a thread under the message instead of the channel itself."
        },
        "reply_broadcast": {
          "label": "Also show the answer in the channel",
          "help": "The first part of a threaded answer also shows in the channel."
        },
        "strict_mention": {
          "label": "Explicit mention in every message",
          "help": "In threads it answers only a message that mentions it explicitly."
        },
        "free_response_rooms": {
          "label": "Rooms with no mention needed",
          "help": "Ids of rooms where it answers every message (they start with !)."
        },
        "allowed_rooms": {
          "label": "Only these rooms",
          "help": "Ids of the rooms it answers in. Direct messages are not affected."
        },
        "dm_mention_threads": {
          "label": "Thread on a mention in a DM",
          "help": "In a direct chat, mentioning it opens a thread."
        },
        "e2ee_mode": {
          "label": "End-to-end encryption",
          "help": "Whether it uses encryption in encrypted rooms."
        },
        "reply_mode": {
          "label": "How it answers",
          "help": "In a thread under the message, or as a plain message in the channel."
        },
        "require_authenticated_sender": {
          "label": "Verify the sender",
          "help": "Only mail your server verified the sender of (SPF/DKIM/DMARC) is accepted, so nobody can pose as an address on the list."
        },
        "skip_attachments": {
          "label": "Ignore attachments",
          "help": "Attachments are not read."
        },
        "poll_interval": {
          "label": "Check mail every (seconds)",
          "help": "Seconds between checks for new mail (5 to 3600)."
        }
      },
      "choice": {
        "unauthorized_dm_behavior": {
          "pair": "Send a pairing code",
          "ignore": "Ignore"
        },
        "reasoning_style": {
          "code": "Code block",
          "blockquote": "Quote",
          "subtext": "Small text"
        },
        "tool_progress": {
          "off": "Off",
          "new": "New tools only",
          "all": "Every tool",
          "verbose": "Every tool, in detail"
        },
        "reply_to_mode": {
          "off": "Never",
          "first": "First message of an answer",
          "all": "Every message"
        },
        "notifications": {
          "important": "Final answers and approvals only",
          "all": "Every message"
        },
        "allow_bots": {
          "none": "Ignore them",
          "all": "Answer them",
          "mentions": "Answer only when mentioned"
        },
        "e2ee_mode": {
          "off": "Off",
          "optional": "When needed",
          "required": "Required"
        },
        "reply_mode": {
          "off": "Plain message",
          "thread": "In a thread"
        }
      },
      "title_of": "{name} settings",
      "platform": {
        "discord": {
          "option": {
            "allowed_users": {
              "label": "People the bot answers",
              "help": "Discord user ids. Anyone not here and without an allowed role gets no answer."
            },
            "require_mention": {
              "label": "In channels: answer only when mentioned",
              "help": "In server channels it answers only a message that @mentions it. DMs are not affected."
            },
            "home_channel": {
              "label": "Home channel",
              "help": "Id of the channel scheduled results and notices go to. /set-home sets it too."
            },
            "reactions": {
              "label": "Reactions",
              "help": "A reaction on your message while it works and when it is done."
            }
          },
          "list_placeholder": "123456789012345678, 876543210987654321"
        },
        "slack": {
          "option": {
            "allowed_users": {
              "label": "People who skip approval",
              "help": "Slack member ids (like U01ABCDEF). Anyone else gets a pairing code."
            },
            "require_mention": {
              "label": "In channels: answer only when mentioned",
              "help": "In channels it answers only a message that mentions it. DMs are not affected."
            },
            "home_channel": {
              "label": "Home channel",
              "help": "Id of the channel (starting with C) scheduled results and notices go to."
            },
            "reactions": {
              "label": "Reactions",
              "help": "A reaction on your message while it works and when it is done."
            }
          },
          "list_placeholder": "C01ABCDEF, C02GHIJKL"
        },
        "matrix": {
          "option": {
            "allowed_users": {
              "label": "People who skip approval",
              "help": "Full Matrix ids (like @sara:matrix.org). Anyone else gets a pairing code."
            },
            "require_mention": {
              "label": "In rooms: answer only when mentioned",
              "help": "In rooms it answers only a message that mentions it. DMs are not affected."
            },
            "home_channel": {
              "label": "Home room",
              "help": "Id of the room (starting with !) scheduled results and notices go to."
            },
            "reactions": {
              "label": "Reactions",
              "help": "A reaction on your message while it works and when it is done."
            }
          },
          "section": {
            "groups": "Rooms"
          },
          "list_placeholder": "!abc:matrix.org"
        },
        "mattermost": {
          "option": {
            "allowed_users": {
              "label": "People who skip approval",
              "help": "Mattermost user ids. Anyone else gets a pairing code."
            },
            "require_mention": {
              "label": "In channels: answer only when mentioned",
              "help": "In channels it answers only a message that @mentions it. DMs are not affected."
            },
            "home_channel": {
              "label": "Home channel",
              "help": "Id of the channel scheduled results and notices go to."
            }
          },
          "list_placeholder": "channelid1, channelid2"
        },
        "email": {
          "option": {
            "allowed_users": {
              "label": "Addresses the agent answers",
              "help": "Email addresses. Mail from anyone else is ignored and gets no pairing code."
            },
            "home_channel": {
              "label": "Home address",
              "help": "The address scheduled results and notices go to."
            }
          },
          "section": {
            "access": "Who may write to the agent",
            "media": "Attachments"
          },
          "list_placeholder": "sara@example.com, ali@example.com"
        }
      }
    },
    "platform": {
      "link_title": "Link {name}",
      "badge_first_use": "Downloaded on first start",
      "badge_inbound": "Needs a public address",
      "how_title": "How to start with {name}",
      "how_pairs": "The first person to message the agent gets a pairing code; approve it under “Approvals” at the top of this page.",
      "how_allowlist": "The agent answers only the people in the allowed list. Change it in Settings.",
      "generic_intro": "Enter the {name} values as Hermes reads them. They are kept secret in this profile's Hermes files.",
      "generic_note": "The hub does not check these values before saving them. If one is wrong, Hermes's message shows on the platform's row once the gateway starts.",
      "first_use_note": "This platform’s library is not in the hub’s image: Hermes downloads it from the internet once, the first time it starts, and keeps it in the data folder.",
      "inbound_note": "This platform delivers messages to a web address the internet must reach, and the hub's image does not open that port by itself. It needs extra setup on the server.",
      "optional": "{label} (optional)",
      "secret_hint": "Kept secret in this profile's Hermes files and never shown again.",
      "allowed": "People who skip approval (optional)",
      "allowed_hint": "Separate them with commas. Anyone else gets a pairing code to approve here.",
      "allowed_hint_generic": "Separate them with commas, as the platform names them. Saved to {key}.",
      "allowlist_empty": "{name} answers nobody who is not on this list, and sends no pairing code. Add yourself at least.",
      "submit": "Link",
      "submit_check": "Check and link",
      "checking": "Asking {name}…",
      "done": "Linked.",
      "done_as": "Linked: {account}.",
      "done_now": "The agent answers on the platform now.",
      "done_restart": "In the default profile, restart Hermes from its card for the agent to start answering.",
      "invalid": "“{field}” is missing or not in the right form.",
      "rejected": "{name} refused these details: {message}",
      "unreachable": "{name} did not answer. Check the address and the hub's internet connection, and try again.",
      "in_use": "This {name} account is linked in the profile “{profile}”. It answers from one place only: unlink it there first.",
      "unlink_body": "The {name} details are removed from this profile and the agent stops answering there. The account itself stays on the platform, and the allowed people and settings stay.",
      "unlinked_note": "{name} unlinked. It no longer answers from this profile.",
      "name": {
        "discord": "Discord",
        "slack": "Slack",
        "matrix": "Matrix",
        "mattermost": "Mattermost",
        "email": "Email",
        "telegram": "Telegram",
        "whatsapp": "WhatsApp",
        "whatsapp_cloud": "WhatsApp Cloud API",
        "teams": "Microsoft Teams",
        "google_chat": "Google Chat",
        "signal": "Signal",
        "line": "LINE",
        "sms": "SMS (Twilio)"
      },
      "field": {
        "DISCORD_BOT_TOKEN": "Bot token",
        "SLACK_BOT_TOKEN": "Bot token (starts with xoxb-)",
        "SLACK_APP_TOKEN": "App token (starts with xapp-)",
        "MATRIX_HOMESERVER": "Homeserver address",
        "MATRIX_ACCESS_TOKEN": "Access token",
        "MATTERMOST_URL": "Mattermost server address",
        "MATTERMOST_TOKEN": "Bot account token",
        "EMAIL_ADDRESS": "Email address",
        "EMAIL_PASSWORD": "Password (or app password)",
        "EMAIL_IMAP_HOST": "IMAP (incoming) server",
        "EMAIL_IMAP_PORT": "IMAP port (default 993)",
        "EMAIL_SMTP_HOST": "SMTP (outgoing) server",
        "EMAIL_SMTP_PORT": "SMTP port (default 587)"
      },
      "discord": {
        "intro": "The agent answers through a Discord bot you make in the developer portal. The hub asks Discord about the bot before it stores the token.",
        "step1": "Open the Discord developer portal, press New Application and pick a name.",
        "step2": "Under Bot, press Reset Token and copy it. On the same page, turn on Message Content Intent.",
        "step3": "Under OAuth2 → URL Generator pick the scopes bot and applications.commands, then Send Messages and Read Message History, and open the link to add the bot to your server.",
        "step4": "Paste the token here, and your Discord user id (turn on Developer Mode, right-click your name → Copy User ID).",
        "allowed": "People the bot answers",
        "allowed_hint": "Discord user ids (digits), separated by commas. Discord sends strangers no pairing code, so anyone not here gets no answer.",
        "how": "Add the bot to your server with the link from the developer portal if you have not, then DM it or @mention it in a channel."
      },
      "slack": {
        "intro": "The agent answers through a Slack app you make. The hub asks Slack about both tokens before it stores them.",
        "step1": "Open api.slack.com/apps, press Create New App → From scratch and pick your Slack team.",
        "step2": "Under Socket Mode, turn it on and create an app-level token with connections:write (it starts with xapp-).",
        "step3": "Under OAuth & Permissions add the bot scopes chat:write, app_mentions:read, channels:history, im:history, im:read, im:write and users:read.",
        "step4": "Under Event Subscriptions, turn them on and subscribe to app_mention, message.im and message.channels; under App Home, turn on the Messages tab.",
        "step5": "Press the Install button on the OAuth & Permissions page, copy the bot token (it starts with xoxb-), and paste both tokens here.",
        "allowed_hint": "Slack member ids (like U01ABCDEF), separated by commas. Anyone else gets a pairing code to approve here.",
        "how": "Invite the app to a channel with /invite, then mention it or message it directly."
      },
      "matrix": {
        "intro": "The agent answers through a Matrix account. Make one for the bot on your homeserver; the hub asks the server about it before saving.",
        "step1": "Create an account for the bot on a Matrix homeserver (in Element, for example).",
        "step2": "Sign in to Element with that account and copy the Access Token from Settings → Help & About.",
        "step3": "Enter the homeserver address (like https://matrix.org) and paste the token here.",
        "allowed_hint": "Full Matrix ids (like @sara:matrix.org), separated by commas. Anyone else gets a pairing code.",
        "how": "Invite the bot's account to a room, or start a direct chat with it. Matrix's library is downloaded on first start."
      },
      "mattermost": {
        "intro": "The agent answers through a Mattermost bot account. The hub asks the server about the account before saving.",
        "step1": "In Mattermost open Integrations → Bot Accounts → Add Bot Account (a system admin turns them on).",
        "step2": "Pick a name for the bot and copy the token shown after it is made.",
        "step3": "Enter your server's address (like https://chat.example.com) and paste the token here.",
        "allowed_hint": "Mattermost user ids, separated by commas. Anyone else gets a pairing code.",
        "how": "Add the bot to a channel and @mention it, or message it directly."
      },
      "email": {
        "intro": "The agent reads a mailbox and answers the mail. The hub signs in to the incoming and outgoing servers before saving; it reads and sends nothing.",
        "step1": "A mailbox just for the agent is best. In Gmail, turn on 2-Step Verification and make an “app password”.",
        "step2": "Enter the address, the password and the IMAP and SMTP servers (in Gmail: imap.gmail.com and smtp.gmail.com).",
        "step3": "Enter the addresses the agent answers. Email sends no pairing code, so anyone not on the list is ignored.",
        "allowed": "Addresses the agent answers",
        "allowed_hint": "Email addresses, separated by commas. Mail from anyone else is ignored.",
        "how": "Send a message to the mailbox from an address on the list; the agent answers in the same thread."
      },
      "badge_program": "Needs the {program} program",
      "program_note": "Hermes runs the {program} program for this platform, and the hub’s image does not include it. Install it on the server first, or the platform reports it missing when the gateway starts."
    },
    "guide": "How to start",
    "picker": {
      "open": "Link a platform",
      "title": "Link a platform",
      "intro": "Pick where the agent answers. Each platform asks for what it needs.",
      "search": "Search platforms",
      "popular": "Popular",
      "more": "More platforms",
      "none": "No platform matches “{query}”."
    },
    "mode": {
      "title": "How will this number be used?",
      "bot": "Bot (a number for the agent)",
      "bot_hint": "People message this number and the agent answers them. Someone writing for the first time gets a pairing code and waits for your approval.",
      "self": "Me (Message yourself)",
      "self_hint": "Your personal number: you talk to the agent in WhatsApp's “Message yourself” chat. Nobody else who messages you reaches the agent or gets a pairing reply.",
      "continue": "Continue",
      "badge_bot": "Bot",
      "badge_self": "Message yourself",
      "change": "Change mode",
      "change_title": "How WhatsApp is used",
      "change_note": "The phone stays linked. The hub restarts Hermes’s WhatsApp connection so the change applies at once.",
      "save": "Save"
    },
    "reply_header": {
      "open": "Reply header",
      "title": "Reply header",
      "note": "In “Message yourself” your messages and the agent’s come from one number, so every reply of the agent starts with this header.",
      "agent_name": "The agent’s name ({name})",
      "agent_name_plain": "The agent’s name",
      "custom": "Custom text",
      "custom_label": "Header text",
      "custom_hint": "One line, up to 64 characters.",
      "preview": "A reply will start like this:",
      "preview_body": "the agent’s reply…",
      "hermes_now": "Now: Hermes’s own header («☤ Hermes Agent»). Saving puts your choice in its place.",
      "no_none": "Hermes always puts a header over replies here, so it can be changed but not removed.",
      "save": "Save"
    },
    "approvals": {
      "open": "Approvals",
      "open_count": "Approvals: {count} waiting",
      "title": "Approvals",
      "close": "Close",
      "waiting_review": "{count} waiting — Review"
    },
    "restart_needed": "Hermes needs a restart to connect {name}.",
    "restart_now": "Restart now",
    "webhooks": {
      "title": "Webhooks",
      "new": "New webhook",
      "intro": "Let an outside service — GitHub, a form, a script — start this agent by sending a request to an address. Each webhook has its own prompt and secret.",
      "public_note": "Outside services send to this hub's address ({origin}). They can reach it only if that address is open to the internet.",
      "public_private": "This page is open on {origin}, an address only your own network or computer can reach. Outside services need a public address for this hub (a domain or a tunnel) before these webhooks can work for them.",
      "none": "No webhooks yet.",
      "listener_online": "Listening",
      "listener_unknown": "Starting",
      "listener_offline": "Not listening",
      "listener_error": "Error",
      "listener_off": "Off",
      "static": "From config.yaml",
      "prompt_label": "Prompt:",
      "prompt_empty": "(the whole request)",
      "url": "Address",
      "secret": "Secret",
      "secret_global": "Signed with the listener's shared secret from config.yaml.",
      "show_secret": "Show secret",
      "hide_secret": "Hide secret",
      "copy": "Copy",
      "copied": "Copied",
      "copy_url": "Copy the address of {name}",
      "copy_secret": "Copy the secret of {name}",
      "test": "Test",
      "testing": "Testing…",
      "test_accepted": "Accepted: the agent is running with this webhook's prompt. This test came from the hub itself, so it does not prove the public address.",
      "test_refused": "The listener answered {status}. {reason}",
      "delete": "Delete",
      "delete_title": "Delete the webhook {name}?",
      "delete_body": "Services that still send to its address will be refused.",
      "new_title": "New webhook",
      "new_intro": "A name for the address, and what the agent is asked when a request arrives.",
      "name": "Name",
      "name_hint": "Lowercase letters, digits, - and _. It is part of the address.",
      "name_invalid": "Use lowercase letters, digits, - and _, starting with a letter or a digit.",
      "name_taken": "A webhook with this name already exists.",
      "prompt": "Prompt",
      "prompt_hint": "{a.b} is replaced with that value of the request's JSON, {event_type} with the event, {__raw__} with the whole request. Leave it empty to send the whole request.",
      "prompt_placeholder": "A new issue was opened: {issue.title}. Read it and suggest a label.",
      "description": "Description (optional)",
      "events": "Events (optional)",
      "events_hint": "Only these events start the agent (GitHub's X-GitHub-Event, or event_type in the request). Empty takes every event.",
      "deliver": "Send the answer to",
      "deliver_log": "Nowhere — keep it in the agent's record",
      "deliver_to": "{name} (its home chat)",
      "create": "Create",
      "creating": "Creating…"
    }
  },
  "question": {
    "title": "Question",
    "recommended": "Recommended",
    "own": "Or write your own answer",
    "skip": "Skip",
    "send": "Send",
    "left": "{time} left",
    "answered": "You answered:",
    "skipped": "Question skipped",
    "expired": "Time ran out without an answer"
  },
  "agent_jobs": {
    "title_of": "{name} jobs",
    "note": "What runs on its own for this agent in this profile. For Hermes these are the jobs in its own scheduler, and Hermes runs them. They are the same schedules the Schedules page shows; make or edit one there.",
    "open_schedules": "Open Schedules",
    "none": "No jobs",
    "none_body": "Nothing is scheduled for this agent in this profile. Make a schedule on the Schedules page and it shows here.",
    "active": "Active",
    "in_hermes": "In Hermes's scheduler",
    "delete_body": "It is deleted from Hermes's scheduler too."
  },
  "agent_plugins": {
    "title_of": "{name} plugins",
    "note": "The plugins Hermes has in this profile, with Hermes's own status. One that is not enabled does not load — except the messaging platforms and model providers Hermes ships, which load when they are used. A change reaches the agent's next session.",
    "none": "No plugins",
    "none_body": "Hermes lists no plugin in this profile.",
    "enabled": "Enabled",
    "status": {
      "enabled": "Enabled",
      "disabled": "Disabled",
      "not_enabled": "Not enabled"
    },
    "source": {
      "bundled": "Ships with Hermes",
      "user": "Installed",
      "external": "From a package"
    },
    "install": "Install",
    "install_label": "Plugin to install",
    "install_placeholder": "chrome-profiles · owner/repo · https://…",
    "install_hint": "A name from Hermes's plugin catalog, owner/repo, or a Git URL. Hermes fetches it, scans it and installs it into this profile switched off; switch it on when you are ready.",
    "installed": "Installed {name}, switched off.",
    "remove": "Remove",
    "remove_title": "Remove {name}?",
    "remove_body": "Its folder is removed from this profile's plugins. Switching it off keeps it.",
    "bundled_refused": "This plugin ships with Hermes; switch it off instead of removing it."
  },
  "trajectory": {
    "tab": "Trajectory",
    "chat_tab": "Chat",
    "tabs_label": "Conversation views",
    "timeline": "Timeline",
    "lane": {
      "input": "Input",
      "model": "Model",
      "tools": "Tools",
      "subagents": "Subagents"
    },
    "kind": {
      "input": "Input",
      "turn": "Turn",
      "reasoning": "Reasoning",
      "tool": "Tool call",
      "subagent": "Subagent"
    },
    "status": {
      "running": "running",
      "failed": "failed",
      "cancelled": "stopped",
      "succeeded": "done"
    },
    "tool_call_only": "tool call only",
    "no_text": "no text",
    "no_details": "Nothing more was recorded for this step.",
    "first_token_here": "First word after {time}",
    "filters": "Filter the steps",
    "filter": {
      "duration": "Duration",
      "turns": "Turns",
      "calls": "Calls"
    },
    "search": "Search the steps",
    "no_match": "No step matches these filters.",
    "download": "Session log",
    "empty_title": "Nothing has happened yet",
    "empty_body": "Send a message and each step the agent takes appears here.",
    "untimed": "This conversation is from before the hub recorded step times, so there is no timeline — only the steps.",
    "not_started": "The turn ended before the agent began, so there is no timeline — only what was sent.",
    "partial": "Earlier turns were recorded before the hub kept step times; the timeline shows the rest.",
    "unit": {
      "ms": "ms",
      "s": "s",
      "min": "min"
    },
    "metric": {
      "turns": "Turns",
      "steps": "Steps",
      "model_time": "Model time",
      "tool_time": "Tool time",
      "first_token": "Avg. time to first token",
      "tokens_per_second": "Tokens/s",
      "cache_hit": "Cache hit",
      "input_tokens": "Input tokens",
      "output_tokens": "Output tokens"
    },
    "model": "Model: {model}",
    "fallback": "Fell back from {failed}"
  },
  "terminal": {
    "warning": "This is a terminal on the server, with the hub account's permissions.",
    "warning_detail": "Whatever you type runs on the hub's host as the hub's own user, in the profile's folder. Opening and closing a terminal is written to the logs.",
    "no_pty": "This hub has no PTY: commands run and print, but there is no line editing and full-screen programs do not work.",
    "idle": "A terminal nobody types in closes after {minutes} minutes. At most {max} are open at once.",
    "new": "New terminal",
    "tab": "Terminal {n}",
    "close": "Close {name}",
    "copy": "Copy",
    "paste": "Paste",
    "copy_hint": "Ctrl+Shift+C copies the selection, Ctrl+Shift+V pastes.",
    "clipboard_denied": "The browser did not allow the clipboard. Use Ctrl+Shift+V or the browser's own paste.",
    "limit": "{max} terminals are open already. Close one first.",
    "empty": "No terminal open",
    "empty_body": "A new terminal starts in the {profile} profile's folder.",
    "disabled": "The terminal is not enabled on this hub.",
    "disabled_body": "It is off unless the hub is started with COREHUB_WEB_TERMINAL=1, and even then only the owner sees it.",
    "connecting": "Connecting to the terminal…",
    "ended": "This terminal has ended: {reason}",
    "reason_exited": "the shell exited",
    "reason_closed": "it was closed",
    "reason_idle": "nobody typed for too long",
    "reason_shutdown": "the hub stopped",
    "gone": "This terminal is no longer on the hub.",
    "folder": "Folder:"
  },
  "this_device": {
    "not_desktop": "This page belongs to the desktop and phone apps.",
    "unavailable": "The app did not answer. Restart it and try again.",
    "connection": "Connection",
    "mode": "Mode",
    "mode_remote": "Connected to a hub",
    "mode_local": "Running on this computer",
    "hub": "Hub address",
    "hub_state": "Hub",
    "hub_checking": "Checking…",
    "hub_unreachable": "Not answering",
    "hub_connected": "Answering (build {version})",
    "app_version": "App version",
    "platform": "System",
    "platform_macos": "macOS",
    "platform_windows": "Windows",
    "platform_linux": "Linux",
    "change": "Change connection",
    "change_hint": "Connect to another hub or switch between a hub and this computer. What each one keeps stays with it.",
    "window": "Window",
    "close_to_tray": "Keep running when the window is closed",
    "close_to_tray_hint": "The app stays in the tray and keeps showing notifications. Quit it from the tray.",
    "no_tray": "This desktop has no tray, so closing the window quits the app.",
    "voice": "Voice",
    "data_dir": "Data folder",
    "hermes": "Hermes Agent",
    "hermes_program": "Installed on this computer",
    "hermes_gateway": "Already running on this computer",
    "hermes_none": "Not found — the agents show Hermes as not installed",
    "hermes_path": "Hermes program",
    "local_sign_in": {
      "title": "Signing in on this computer",
      "label": "Sign in without a password on this computer",
      "hint": "The app signs you, the owner, in to the hub on this computer without the password. Phones, browsers and other computers still need it.",
      "refused": "Only the owner, signed in, can turn this on. Sign in again and try once more.",
      "recovery_on": "If you forget the password, “Forgot password?” on the sign-in screen resets it after this computer confirms it is you.",
      "recovery_off": "This computer has no way to confirm it is you (such as Windows Hello or a password prompt), so “Forgot password?” is not offered here."
    }
  },
  "helper": {
    "title": "Agents on this computer",
    "intro": "Lets an agent use this computer — only the folders and programs you share, only as you allow — from the hub on this computer or from a hub on a server. Off until you turn it on.",
    "enable": "Let agents use this computer",
    "enable_hint": "It listens on this computer only and wants its key on every request.",
    "exposed": "What agents can do",
    "tool_list_allowed_folders": "See which folders are shared",
    "tool_list_directory": "List what is inside a shared folder",
    "tool_read_text_file": "Read text files in shared folders",
    "tool_write_text_file": "Write text files in folders shared as writable",
    "tool_open_path": "Open files in shared folders with their app",
    "tool_open_url": "Open web links in your browser",
    "folders": "Shared folders",
    "no_folders": "No folder is shared: agents can see nothing on this computer.",
    "add_folder": "Share a folder…",
    "remove": "Stop sharing",
    "writable": "Agents may write here",
    "allow_open": "Allow opening files and web links",
    "allow_open_hint": "Files only inside the shared folders; links only http and https.",
    "connection": "Connection",
    "address": "Address",
    "token": "Key",
    "copy": "Copy",
    "copied": "Copied",
    "show": "Show",
    "hide": "Hide",
    "new_token": "New key",
    "new_token_hint": "Whatever uses the old key stops working until it gets the new one.",
    "add_to_hermes": "Add to Hermes on this hub",
    "added_to_hermes": "Added to Hermes as “{name}”. It takes effect on Hermes’s next start.",
    "no_hermes_agent": "This hub has no Hermes agent to add it to.",
    "remote_note": "A hub on a server reaches this computer through its device connection, which this app keeps open while it runs, also from the tray.",
    "activity": "Recent use",
    "no_activity": "Nothing has used the helper yet.",
    "refused": "refused",
    "not_running": "The helper is on but not running: {message}",
    "local_note": "The hub on this computer reaches the helper at this address. Add it to Hermes once; each profile gets the programs you switched on for it.",
    "default_folder": "Core Hub folder",
    "default_folder_hint": "The app made this folder for Core Hub's files when you turned the helper on. Programs put what they make here (a render, say) unless you say otherwise. Stop sharing it any time.",
    "via_hub": "from the hub",
    "via_local": "from this computer"
  },
  "desktop_updates": {
    "title": "Updates",
    "auto": "Look for new versions on its own",
    "auto_hint": "About ten seconds after Core Hub starts, then every six hours. A new version downloads in the background; Core Hub restarts only when you say so.",
    "check": "Check now",
    "checking": "Checking…",
    "current": "This version: {version}",
    "available": "Core Hub {version} is available.",
    "download": "Download",
    "notes": "What's new",
    "up_to_date": "This is the newest version for this computer.",
    "failed": "Could not check: {message}",
    "checked_at": "Checked {time}",
    "all_releases": "All releases",
    "from_store": "Updates come from the Microsoft Store.",
    "store_page": "Open in the Microsoft Store",
    "size": "{size} MB",
    "auto_hint_notify": "About ten seconds after Core Hub starts, then every six hours. Core Hub tells you when a new version is out; nothing is downloaded.",
    "ready": "Core Hub {version} is ready to install.",
    "ready_hint": "If you don't restart now, it is installed the next time you quit Core Hub.",
    "restart": "Restart to update",
    "later": "Later",
    "downloading": "Downloading Core Hub {version}…",
    "downloading_percent": "Downloading Core Hub {version}… {percent}%",
    "download_page": "Open the download page",
    "available_hint": "Get it from the download page and install it over this one.",
    "notice_label": "New version of Core Hub"
  },
  "rooms": {
    "filter": "Which rooms",
    "active": "Active",
    "archived": "Archived",
    "none": "No rooms yet. Make one, or join one with its code.",
    "none_archived": "No archived rooms.",
    "counts": "{agents} agents · {people} people",
    "pick_title": "Open a room",
    "pick_body": "Pick a room from the list, make a new one, or join one by its code.",
    "agent_badge": "Agent",
    "archive": "Archive",
    "unarchive": "Bring back",
    "archived_notice": "This room is archived: its messages stay readable, and nothing new can be written in it.",
    "back": "Back to rooms",
    "deleted": "This room was deleted.",
    "removed": "You are no longer a member of this room.",
    "delete_title": "Delete this room?",
    "delete_body": "“{name}”, its messages and its seats go for good.",
    "leave": "Leave room",
    "leave_title": "Leave this room?",
    "leave_body": "You leave “{name}”. To come back you need its code again.",
    "rename": "Rename",
    "empty": "Nothing said yet. Write to the room; mention an agent with @ to ask it.",
    "empty_no_seats": "No agents in this room yet. Add one from the members panel.",
    "older": "Show older messages",
    "handoff_to": "Passed the turn to @{name}",
    "activity": {
      "queued": "{name} is next",
      "thinking": "{name} is thinking",
      "working": "{name} is replying",
      "waiting": "{name} is waiting for an approval",
      "typing": "{names} typing…"
    },
    "status": {
      "idle": "Idle",
      "queued": "Queued",
      "thinking": "Thinking",
      "running": "Replying",
      "waiting_approval": "Waiting for approval",
      "offline": "Offline"
    },
    "composer": {
      "label": "Message to the room",
      "placeholder": "Write to the room — @ to mention an agent",
      "mentions": "Agents to mention",
      "all": "every agent",
      "lead_hint": "A message that mentions nobody goes to @{name}.",
      "no_lead_hint": "A message that mentions nobody is only read — mention an agent with @."
    },
    "invite": {
      "button": "Invite",
      "title": "Invite to the room",
      "description": "Anyone who can enter this profile joins with this code or link. A new code retires the old one.",
      "rotate": "New code",
      "copy": "Copy link",
      "copied": "Copied"
    },
    "join": {
      "description": "Paste the room's code or its link.",
      "code": "Code or link",
      "join": "Join",
      "open": "Open the room",
      "unknown": "No room you can join has this code."
    },
    "new": {
      "description": "You manage the room you make. Choose the agents to seat now; you can add more later.",
      "name": "Room name",
      "agents": "Agents",
      "create": "Make the room",
      "no_agents": "No agent is installed in this profile yet.",
      "seat_failed": "The room was made, but these seats were not: {list}"
    },
    "members": {
      "title": "Members",
      "agents": "Agents",
      "people": "People",
      "lead": "Lead",
      "make_lead": "Make lead",
      "no_agents": "No agents yet.",
      "owner": "Manager",
      "online": "In the room",
      "offline": "Away",
      "remove": "Remove",
      "default_model": "the agent's own model",
      "stop": "Stop @{name}"
    },
    "seat": {
      "add": "Add agent",
      "add_title": "Add an agent to the room",
      "edit_title": "Edit the seat",
      "description": "Each agent in a room has its own name, role, instructions and model.",
      "agent": "Agent",
      "name": "Name in the room",
      "name_hint": "People mention it as @name; unique in the room.",
      "role": "Role",
      "instructions": "Instructions",
      "instructions_hint": "Given to this agent on every turn in this room.",
      "model": "Model",
      "model_hint": "Leave empty for the agent's own model.",
      "remove": "Remove",
      "remove_title": "Remove this agent from the room?",
      "remove_body": "@{name} leaves the room; what it said stays."
    },
    "handoff": {
      "active": "@{from} passed the turn to @{to} (pass {depth})",
      "stopped_max_depth": "Stopped passing the turn: @{from} → @{to} would go past the room's limit.",
      "stopped_loop_detected": "Stopped passing the turn: @{from} → @{to} would repeat a pass already made.",
      "stopped_interrupted": "Passing the turn was stopped at @{from} → @{to}.",
      "stopped_error": "Passing the turn stopped: @{to} could not answer.",
      "continue": "One more round"
    },
    "clear": {
      "menu": "Clear the context",
      "title": "Clear the room's context?",
      "body": "The messages stay, but the agents forget them: each starts a fresh conversation, and the summary and token count start over.",
      "confirm": "Clear"
    },
    "settings": {
      "title": "Room settings",
      "mention_all": "Allow @all (every agent at once)",
      "handoff": "Agents may pass the turn to each other",
      "max_depth": "Most passes in a row",
      "max_depth_hint": "1–20; empty for no limit. A pass repeated in the same run always stops.",
      "project": "Project reporting here",
      "project_none": "No project",
      "project_hint": "When a task of this project starts or ends, the room is told."
    },
    "memory": {
      "title": "Summary",
      "none": "No summary yet. It is written every few messages, and agents are told it on every turn.",
      "refresh": "Summarise now",
      "edit": "Edit summary",
      "working": "Summarising…",
      "failed": "Could not summarise"
    }
  },
  "voice": {
    "dictate": "Dictate",
    "stop_dictation": "Stop and transcribe",
    "requesting": "Waiting for the microphone…",
    "listening": "Listening… {seconds}s",
    "transcribing": "Transcribing…",
    "cancel": "Cancel",
    "browser_engine": "With this browser’s own recognizer: the profile has no speech-to-text provider.",
    "error": {
      "no_provider": "Dictation needs a speech-to-text provider, and this browser has no recognizer of its own.",
      "mic_denied": "The browser was not allowed to use the microphone.",
      "mic_unavailable": "No microphone could be opened.",
      "no_speech": "Nothing was heard. Try again a little closer to the microphone.",
      "failed": "Dictation failed: {detail}"
    },
    "open_models": "Set one up in Models",
    "menu": "Voice options",
    "language": "Dictation language",
    "language_auto": "Detect automatically",
    "language_ar": "العربية",
    "language_en": "English",
    "auto_read": "Read replies aloud",
    "mode": "Voice mode",
    "speak": "Read aloud",
    "stop_speaking": "Stop reading",
    "loading_audio": "Preparing the voice…",
    "browser_voice": "Read aloud with this browser’s voice (the profile has no text-to-speech provider)",
    "no_tts": "No text-to-speech provider is ready for this profile.",
    "stage": {
      "title": "Voice mode",
      "turn_by_turn": "Turn by turn: the hub’s speech providers don’t stream, so your words go when you stop talking, and the reply is spoken sentence by sentence as it arrives.",
      "ready": "Tap to talk",
      "listening": "Listening",
      "transcribing": "Transcribing",
      "thinking": "Thinking",
      "speaking": "Speaking",
      "tap_to_send": "Tap to send",
      "interrupt": "Interrupt",
      "close": "End voice mode",
      "hint": "Tap to start and tap again to send — or hold, and let go to send.",
      "you_said": "You said:"
    }
  },
  "perf": {
    "live": "Live",
    "paused": "Paused while this tab is hidden",
    "every": "Updates every {count} s",
    "at": "Measured at {time}",
    "no_proc": "This host has no /proc, so the Hermes processes are listed without their numbers.",
    "host": "Host",
    "hub": "Hub process",
    "cpu": "CPU",
    "cores": "{count} cores",
    "memory": "Memory",
    "of": "{used} of {total}",
    "load": "Load",
    "load_hint": "1 · 5 · 15 minutes",
    "one_core": "Of one core",
    "rss": "Memory (RSS)",
    "heap": "Heap {size}",
    "lag": "Event-loop lag",
    "ms": "{count} ms",
    "uptime": "Uptime",
    "hermes": "Hermes processes",
    "hermes_rss": "Memory of every Hermes process",
    "hermes_none": "The hub runs no Hermes process right now.",
    "kind_tui_gateway": "TUI gateway (conversations)",
    "kind_dashboard": "hermes serve",
    "kind_gateway": "Messaging gateway · {profile}",
    "process": "Process",
    "state": "State",
    "profiles": "Profiles",
    "profile": "Profile",
    "active_runs": "Active runs",
    "sessions": "Conversations",
    "sockets": "Sockets",
    "dh": "{d}d {h}h",
    "hm": "{h}h {m}m",
    "m": "{m}m",
    "s": "{s}s"
  },
  "logview": {
    "kept": "The latest lines of the hub and of every Hermes gateway, kept in the hub's memory — a restart empties them.",
    "source": "Source",
    "source_all": "All",
    "source_hub": "Hub",
    "source_hermes": "Hermes",
    "source_errors": "Errors only",
    "profile": "Profile",
    "all_profiles": "Every profile",
    "level": "Level",
    "level_debug": "Everything",
    "level_info": "Info and above",
    "level_warn": "Warnings and errors",
    "level_error": "Errors",
    "level_short_error": "ERROR",
    "level_short_warn": "WARN",
    "level_short_info": "INFO",
    "level_short_debug": "DEBUG",
    "search": "Search the lines",
    "lines": "Lines",
    "last_n": "Last {count}",
    "tail": "Live tail",
    "tail_paused": "Paused while this tab is hidden",
    "download": "Download",
    "no_match": "No line matches.",
    "empty": "No lines yet.",
    "showing": "{count} lines shown",
    "hub": "Hub",
    "tui": "Hermes · TUI gateway",
    "hermes_of": "Hermes · {profile}"
  },
  "files": {
    "button": "Files",
    "button_count": "Files ({count})",
    "title": "Files",
    "list_label": "Files of this conversation",
    "empty": "No files yet. What the agent writes in this conversation's folder, and what is attached, appears here.",
    "truncated": "The folder holds more files than are listed; the newest are shown.",
    "load_failed": "The files could not be read.",
    "panel_label": "Open files",
    "close_tab": "Close {name}",
    "open": "Open {name}",
    "download": "Download",
    "open_new_tab": "Open in new tab",
    "view_source": "View source",
    "view_rendered": "View page",
    "loading": "Loading the file…",
    "too_large": "This file is too large to show here ({size}). Download it to open it.",
    "no_preview": "This kind of file cannot be shown here. Download it to open it.",
    "source": {
      "tool": "written by a tool",
      "working_dir": "in the folder",
      "attachment": "attached"
    },
    "rows_capped": "Showing the first {shown} of {total} rows.",
    "columns_capped": "Only the first {count} columns are shown.",
    "sheets_label": "Sheets",
    "slide": "Slide {number}",
    "untitled_slide": "Untitled slide",
    "pptx_outline": "An outline of the slides' titles and text. The slides themselves are not drawn here; download the file to see them.",
    "docx_note": "The text of the document; pictures and page layout are not shown.",
    "office_error": {
      "too_many_entries": "The file holds too many parts to open safely.",
      "too_large": "The file unpacks to more than can be opened safely.",
      "not_office": "This is not the Office file its name says.",
      "damaged": "The file is damaged and cannot be read."
    },
    "html_note": "Shown in a sandbox: its scripts run apart from this app and it cannot contact anything.",
    "modified": "Changed {time}",
    "updated": "Updated just now",
    "gone": "This file is no longer in the conversation's folder.",
    "close": "Close the files",
    "cannot_play": "This browser cannot play this file. Download it to open it in a player."
  },
  "workspace_files": {
    "intro": "The working folder of the profile «{profile}»: the folders its conversations and tasks work in. A file manager only — nothing here runs a command.",
    "root": "{profile}",
    "breadcrumb": "Folder path",
    "caption": "Files in {folder}",
    "name": "Name",
    "size": "Size",
    "modified": "Modified",
    "actions": "Actions",
    "actions_for": "Actions for {name}",
    "up": "Up one folder",
    "empty": "This folder is empty",
    "empty_body": "Drop files here, or use Upload, New folder or New file.",
    "truncated": "This folder holds more than 5,000 items; only the first 5,000 are listed.",
    "limits": "Limits: upload {upload} per file · edit {edit} · zip {zip}.",
    "new_folder": "New folder",
    "new_file": "New file",
    "new_name": "Name",
    "create": "Create",
    "upload": "Upload",
    "uploading": "Uploading {names}…",
    "uploaded": "Uploaded {count} file(s)",
    "drop_zone": "Folder contents; drop files here to upload",
    "drop_here": "Drop to upload into this folder",
    "replace_title": "Replace «{name}»?",
    "replace_body": "A file with this name is already in this folder. Replacing it cannot be undone.",
    "replace": "Replace",
    "download": "Download",
    "download_zip": "Download as zip",
    "preview": "Preview",
    "preview_of": "Preview of {name}",
    "no_preview": "No preview for this kind of file",
    "no_preview_body": "Download it to open it on your device.",
    "pdf_fallback": "This browser cannot show the PDF here. Download it instead.",
    "close": "Close",
    "attach": "Attach to chat",
    "attach_title": "Attach «{name}» to a chat",
    "attach_body": "A copy of the file goes into the chat's message box, ready to send. The file here stays as it is.",
    "attach_new_chat": "New chat",
    "rename_title": "Rename «{name}»",
    "move": "Move",
    "copy": "Copy",
    "move_title": "Move «{name}»",
    "copy_title": "Copy «{name}»",
    "destination": "Destination path",
    "destination_hint": "Inside this profile's files, for example reports/2026/plan.md. The folder must exist.",
    "moved": "Moved to {path}",
    "copied": "Copied to {path}",
    "delete_title": "Delete «{name}»?",
    "delete_body": "The file is removed from the profile's folder. This cannot be undone.",
    "delete_folder_body": "The folder and everything in it are removed from the profile's folder. This cannot be undone.",
    "deleted": "Deleted {name}",
    "failed": "That did not work",
    "back_to_top": "Back to the top folder",
    "link": "Link",
    "link_outside": "Link outside",
    "link_outside_hint": "This link points outside the profile's files, so it cannot be opened here.",
    "edit_title": "Edit {name}",
    "new_file_title": "New file {name}",
    "editor_label": "Text of {name}",
    "saved": "Saved {name}",
    "unsaved": "Unsaved changes",
    "all_saved": "All changes saved",
    "conflict": "The file changed on disk after you opened it (an agent or another tab wrote it). Nothing was saved.",
    "conflict_reload": "Load the new version (discard my edits)",
    "conflict_overwrite": "Keep my text and overwrite",
    "discard_title": "Discard your changes?",
    "discard_body": "Your edits to {name} are not saved.",
    "discard": "Discard"
  },
  "changes": {
    "label": "Files this run changed",
    "summary": {
      "zero": "Changed no files",
      "one": "Changed one file",
      "two": "Changed two files",
      "few": "Changed {count} files",
      "many": "Changed {count} files",
      "other": "Changed {count} files"
    },
    "kind": {
      "added": "new",
      "modified": "changed",
      "deleted": "deleted",
      "renamed": "moved"
    },
    "binary": "binary",
    "open_diff": "Show what this run changed in {name}",
    "diff_tab": "{name} (changes)",
    "show_all": "Show all {count}",
    "show_less": "Show fewer",
    "unlisted": "And more files not listed here ({count}).",
    "incomplete": "The folder is larger than the hub reads when a run starts, so a change may be missing here.",
    "live": "So far"
  },
  "diff": {
    "layout": "How to show the changes",
    "unified": "Unified",
    "split": "Side by side",
    "open_file": "Open the file",
    "open_file_named": "Open {name}",
    "loading": "Loading the changes…",
    "renamed_from": "Moved from",
    "truncated": "The changes are cut here to stay under the size limit.",
    "no_lines": "Moved without changing its lines.",
    "state": {
      "available": "There are no lines to show.",
      "binary": "A binary file: there are no lines to compare.",
      "too_large": "The changes to this file are too large to show here.",
      "unavailable": "The hub saw this file change but kept no copy of it from before the run, so it cannot show the lines."
    }
  },
  "subagents": {
    "title": "Subagents",
    "running_count": "{count} running",
    "finished": "Finished ({count})",
    "stop": "Stop",
    "steer": "Steer",
    "steer_placeholder": "Tell it what to change…",
    "steer_send": "Send",
    "steer_queued": "Sent. It reads it before its next step.",
    "steer_rejected": "It no longer takes guidance.",
    "view": "View",
    "tools": "{count} tools",
    "last_tool": "last: {tool}",
    "status": {
      "running": "Running",
      "completed": "Done",
      "failed": "Failed",
      "interrupted": "Stopped"
    },
    "observe_note": "This agent only says when its subagents start and end.",
    "sheet_title": "Subagent",
    "close": "Close",
    "status_label": "Status",
    "model": "Model",
    "elapsed": "Time",
    "tool_count": "Tools",
    "summary": "Last words",
    "recent_tools": "Recent tools",
    "output": "Live output",
    "no_output": "Nothing to show yet.",
    "output_truncated": "Only the end is shown.",
    "open_trajectory": "Open in Trajectory"
  },
  "background": {
    "open": "Background",
    "open_count": "Running in the background: {count}",
    "title": "Background",
    "close": "Close",
    "none": "Nothing is running.",
    "none_body": "Chats, tasks, schedules, workflows, jobs and subagents appear here while they work.",
    "finished": "Finished in the last 24 hours ({count})",
    "stop": "Stop",
    "open_item": "Open",
    "kind": {
      "chat_run": "Chat",
      "task_run": "Task",
      "schedule_run": "Schedule",
      "workflow_run": "Workflow",
      "job": "Job",
      "subagent": "Subagent"
    },
    "status": {
      "queued": "Queued",
      "running": "Running",
      "succeeded": "Done",
      "failed": "Failed",
      "cancelled": "Stopped"
    }
  },
  "config_files": {
    "title_of": "{name} config files",
    "shared": "These files are shared by every profile: coding agents read them from the home of the user Core Hub runs as, whichever profile a conversation is in. A change here applies everywhere, from the agent's next conversation.",
    "none": "This agent has no config files Core Hub can edit.",
    "file_instructions": "Instructions",
    "file_settings": "Settings",
    "about_instructions": "What the agent reads at the start of every conversation, in every project.",
    "about_settings": "The agent's own settings, read when a conversation starts. A settings file can hold keys: only owners and admins see this page.",
    "not_yet": "Not created yet",
    "unsaved": "Unsaved changes",
    "revert": "Revert",
    "reload": "Load the current file",
    "saved": "Saved. The agent reads it from its next conversation; the previous version is kept as a backup on the hub.",
    "changed": "The file changed after you opened it (by hand, or by the agent). Nothing was written.",
    "invalid_json": "This is not valid JSON, so nothing was written: {message}",
    "symlink_outside": "This file is a link to somewhere outside the agent's folder and the home, so Core Hub will not open it.",
    "not_text": "This file is not UTF-8 text, so it cannot be edited here.",
    "too_large": "The file is larger than 1 MiB, the most this page edits.",
    "empty_placeholder": "The file is empty. Write its settings here; the file is made when you save."
  },
  "channel_accounts": {
    "title": "Messaging accounts",
    "subtitle": "Link your own Telegram or WhatsApp so the agent's Core Hub tools act as you when you message its bot. A message from an account nobody linked gets none of them, and a group chat none either.",
    "none": "No messaging account is linked to you.",
    "platform": "Platform",
    "account": "Account",
    "linked_at": "Linked",
    "last_used": "Last used",
    "actions": "Actions",
    "platform_telegram": "Telegram",
    "platform_whatsapp": "WhatsApp",
    "link": "Link an account",
    "send_this": "From the Telegram or WhatsApp account you want to link, send this to your agent's bot:",
    "copy": "Copy",
    "copied": "Copied",
    "expires": "The code works once, until {time}.",
    "must_answer": "The bot must already answer you (paired, or on its allowed list). It replies when the account is linked.",
    "waiting": "Waiting for the message…",
    "linked": "Linked. Messages from that account now use Core Hub's tools as you.",
    "unlink": "Unlink",
    "unlink_title": "Unlink this account?",
    "unlink_body": "Its next message gets none of Core Hub's tools. The agent still answers it.",
    "all_title": "Linked messaging accounts",
    "all_subtitle": "Each person's Telegram and WhatsApp accounts. A message from one acts as that person, in the profiles they may enter.",
    "all_none": "Nobody has linked a messaging account.",
    "remove": "Remove",
    "remove_title": "Remove {name}'s link?"
  },
  "device_link": {
    "title": "Connection to this hub",
    "intro": "A hub on a server can use this computer's shared folders and programs only once this computer is linked to it as a device. The app then keeps its own connection, even when the window is closed; nothing listens on this computer.",
    "link": "Link this computer to the hub",
    "refused": "The hub no longer accepts this computer (it was unlinked there). Link it again.",
    "profiles": "Profiles that may use this computer",
    "profiles_hint": "All your profiles until you narrow it. A profile left out cannot reach this computer's folders or programs.",
    "unlink": "Unlink this computer",
    "status_connected": "Connected",
    "status_connecting": "Connecting…",
    "status_offline": "Offline — trying again",
    "status_refused": "Refused",
    "status_stopped": "Stopped",
    "status_unlinked": "Not linked"
  },
  "programs": {
    "title": "Programs on this computer",
    "count": "{on} of {all} on",
    "intro": "Programs that registered an AI integration (an MCP server) with another assistant on this computer: Claude Desktop and its extensions, Claude Code, Codex, Cursor or Windsurf. Each is off until you pick the profiles it serves; it runs with your account's rights, and the computer asks you once per session before an agent uses it.",
    "rescan": "Look again",
    "empty": "No program found. Programs register with those assistants: install the program's AI integration there, then look again.",
    "source": {
      "claude_desktop": "Claude Desktop",
      "claude_desktop_extension": "Claude Desktop extension",
      "claude_code": "Claude Code",
      "codex": "Codex",
      "cursor": "Cursor",
      "windsurf": "Windsurf"
    },
    "status": {
      "ready": "Ready",
      "needs_setup": "Needs setup",
      "remote": "Online server",
      "invalid": "Cannot start"
    },
    "on_for": "On for {count}",
    "found_in": "Found in",
    "remote_note": "This one is a server on the network, not a program on this computer; it is not passed through in this version.",
    "invalid_note": "Its registration names no program to start.",
    "settings": "Settings",
    "needs_setup_hint": "It needs a value only you have, which the other assistant kept to itself. Give it here; the app keeps secrets sealed by your system’s keychain.",
    "save": "Save",
    "clear": "Remove",
    "saved_secret": "Saved — type to replace",
    "profiles": "Profiles that may use it",
    "profiles_hint": "None: off (the default).",
    "failed": "It did not start: {message}",
    "tools": "What it offers",
    "consent_note": "Before an agent uses a program, this computer asks you: allow once, allow for this session, or deny. Every call appears under Recent use.",
    "resolve": {
      "title": "DaVinci Resolve",
      "ready": "Ready",
      "not_ready": "Not ready yet",
      "check": "Check",
      "intro": "Checks that Resolve’s integration is here, Resolve is running, scripting from outside is allowed and it is the Studio edition.",
      "integration": "Integration",
      "integration_missing": "not found",
      "integration_found": "found, not switched on",
      "integration_shared": "found and switched on",
      "running": "Running",
      "unknown": "could not tell",
      "yes": "yes",
      "no": "no",
      "scripting": "External scripting",
      "scripting_reachable": "answers",
      "scripting_unreachable": "does not answer",
      "scripting_unknown": "not checked",
      "edition": "Edition",
      "step_install_integration": "Install DaVinci Resolve’s AI integration in Claude Desktop (or another assistant listed above), then press Look again.",
      "step_share_program": "Switch DaVinci Resolve on above for the profiles that should use it.",
      "step_start_resolve": "Open DaVinci Resolve and keep it running: its scripting talks to the running app.",
      "step_enable_scripting": "In Resolve: Preferences → System → General → External scripting using → Local. Then restart Resolve.",
      "step_needs_studio": "Scripting from outside Resolve needs DaVinci Resolve Studio; the free edition allows it only from inside the app.",
      "support": "DaVinci Resolve support and downloads"
    }
  },
  "desktop_voice": {
    "intro": "Dictation and reading aloud work here as in a browser: the microphone records on this computer, and your hub writes the words out and speaks the replies with the profile's speech providers.",
    "mic_label": "Microphone:",
    "mic_granted": "Allowed",
    "mic_granted_hint": "the system lets Core Hub use it.",
    "mic_denied": "Blocked",
    "mic_denied_hint": "the system blocks it for Core Hub. Allow Core Hub in the system's microphone settings, then try again.",
    "mic_not_determined": "Not asked yet",
    "mic_not_determined_hint": "the system asks you the first time you dictate, or now.",
    "mic_restricted": "Restricted",
    "mic_restricted_hint": "a policy on this computer does not let apps use it.",
    "mic_unknown": "No system switch",
    "mic_unknown_hint": "this system has no per-app switch; Core Hub asks for it when you dictate.",
    "ask": "Allow the microphone",
    "open_settings": "Open the system's microphone settings",
    "no_providers": "This profile has no speech-to-text or text-to-speech provider ready, so there is nothing to dictate to or read aloud with.",
    "no_stt": "This profile has no speech-to-text provider ready: the test only says whether sound was heard.",
    "no_tts": "This profile has no text-to-speech provider ready, so replies cannot be read aloud.",
    "test": "Test the microphone",
    "testing": "Speak now… ({seconds} seconds)",
    "level": "Sound level",
    "heard_text": "The hub heard: “{text}”",
    "heard_sound": "Sound came through the microphone.",
    "heard_nothing": "Nothing was heard. Check that the right microphone is chosen in the system and speak a little closer.",
    "speak_test": "Test reading aloud",
    "sample": "This is how Core Hub reads your replies aloud.",
    "speak_failed": "The hub could not read the sentence aloud. Check the profile's text-to-speech provider in Models."
  },
  "outside": {
    "title": "Reach from outside",
    "intro": "The hub on this computer answers this computer only. To use it from your phone away from home, open a way in on an account of your own: a Cloudflare Tunnel, or your Tailscale network. Core Hub runs no service for you.",
    "warning": "Turning this on makes your hub reachable from the internet through your own tunnel. Anyone who reaches it still has to sign in, so use a strong password; turn it off when you do not need it.",
    "route": "How phones reach it",
    "cloudflare": "Cloudflare Tunnel",
    "cloudflare_hint": "A public address on a domain in your Cloudflare account. Works on any network.",
    "tailscale": "Tailscale",
    "tailscale_hint": "Only devices on your own tailnet can reach it. Needs Tailscale on this computer and on the phone.",
    "cf_step_create": "In your Cloudflare dashboard open Zero Trust → Networks → Tunnels, and create a tunnel of the Cloudflared kind.",
    "cf_step_route": "Give it a public hostname whose service is",
    "cf_step_token": "Copy the tunnel's token (the long text after --token in the install command) and paste it below. Core Hub downloads Cloudflare's own cloudflared once and runs it.",
    "copy": "Copy",
    "copied": "Copied",
    "token_kept": "A token is kept in this computer's keychain (tunnel {tunnel}).",
    "forget_token": "Forget the token",
    "token": "Tunnel token",
    "token_replace": "New tunnel token (optional)",
    "token_hint": "Kept in this computer's keychain; it is never shown again or sent anywhere but to cloudflared.",
    "hostname": "Public hostname",
    "hostname_hint": "The hostname you gave the tunnel, such as hub.example.com. Phones pair with it.",
    "route_elsewhere": "Points elsewhere — set the service to {service}",
    "tailnet_found": "This computer is on a tailnet at",
    "tailnet_missing": "This computer is not on a Tailscale network. Install Tailscale, sign in, then come back.",
    "tailscale_phone": "Core Hub listens on this tailnet address only. Install Tailscale on the phone with the same account, then pair it.",
    "turn_on": "Turn on",
    "turn_off": "Turn off",
    "save": "Save",
    "connected": "Open. Phones pair at {url}.",
    "pair_phone": "Pair a phone",
    "no_hostname": "The tunnel is connected, but it has no public hostname yet. Add one in Cloudflare and write it above.",
    "state_off": "Off",
    "state_connected": "Connected",
    "state_connecting": "Connecting…",
    "state_error": "Not working",
    "error": {
      "token_invalid": "Cloudflare did not accept the token. Copy it again from the tunnel's page.",
      "download_failed": "cloudflared could not be downloaded from Cloudflare's releases. Check the internet connection and try again.",
      "checksum_mismatch": "The downloaded cloudflared is not the file Cloudflare published, so it was not run.",
      "unsupported_platform": "Cloudflare publishes no cloudflared for this computer.",
      "start_failed": "cloudflared did not start.",
      "tunnel_exited": "cloudflared stopped; Core Hub is starting it again.",
      "not_on_tailnet": "This computer is not on a Tailscale network.",
      "listen_failed": "Core Hub could not listen on the tailnet address."
    },
    "refused": {
      "token_invalid": "That is not a Cloudflare tunnel token. Paste only the long text after --token.",
      "token_required": "Paste the tunnel token first.",
      "route_required": "Choose Cloudflare Tunnel or Tailscale first.",
      "hostname_invalid": "That is not a hostname. Write it like hub.example.com.",
      "relay_unavailable": "Only the hub the desktop app runs on this computer can open a way in.",
      "relay_host_not_answering": "The desktop app did not answer. Try again in a moment."
    }
  },
  "presets": {
    "title": "Presets",
    "subtitle": "This agent's settings in this profile, saved under a name: the chat model and its fallbacks, the agent's own model, which skills and MCP servers are on, and the settings below. Never a key or a password.",
    "name": "Name for the current settings",
    "save": "Save current settings",
    "none": "No presets yet.",
    "activate": "Activate",
    "activate_title": "Activate “{name}”?",
    "activate_body": "Its settings replace this agent's current ones in this profile. What must restart for them restarts.",
    "delete_title": "Delete “{name}”? The agent's settings do not change.",
    "applied": "Applied.",
    "applied_partly": "Applied, except what is gone since it was saved: {skipped}.",
    "restarting": "Restarting what the settings need.",
    "separator": ", ",
    "part": {
      "model": "Model",
      "skills": "{count} skills",
      "mcp_servers": "{count} MCP servers",
      "settings": "{count} settings sections"
    },
    "part_name": {
      "model": "the model",
      "skills": "the skills",
      "mcp_servers": "the MCP servers",
      "settings": "the settings"
    }
  },
  "linked_hubs": {
    "intro": "Link this hub with another Core Hub. A link lets each side see the names of the agents the other shares, and lets a person here ask one of them a single question — answered by that agent's model, without tools, files or memory. Nothing else is shared: no files, memory, tasks, providers or keys. Both owners must agree.",
    "invite_title": "Invite another hub",
    "invite_hint": "Make a link and send it to the other hub's owner. It works once, for 10 minutes.",
    "invite_create": "Make an invite link",
    "invite_again": "Make another",
    "invite_expires": "Works once, until {time}.",
    "own_fingerprint": "This hub's key",
    "their_fingerprint": "Their key",
    "copy": "Copy",
    "redeem_title": "Use an invite",
    "redeem_hint": "Paste the link another hub's owner sent you. Their owner then approves.",
    "redeem_url": "Invite link",
    "redeem_name": "Name for it here (optional)",
    "redeem_submit": "Ask to link",
    "redeem_sent": "Asked. The link is ready once their owner approves.",
    "peers_heading": "Hubs",
    "none": "No linked hubs.",
    "enabled": "On",
    "off": "Off",
    "approve": "Approve",
    "refuse": "Refuse",
    "unlink": "Unlink",
    "unlink_title": "Unlink “{name}”?",
    "unlink_body": "Its key is forgotten at once and it can no longer ask anything here. The log stays.",
    "rename": "Rename",
    "rename_title": "Rename the hub",
    "rename_label": "Name",
    "asks_per_hour": "Questions it may ask per hour",
    "their_agents": "Their shared agents",
    "their_agents_none": "They share no agents.",
    "ask_hint": "One question, one answer. Their agent answers without tools, files or memory.",
    "ask_agent": "Agent",
    "ask_question": "Your question",
    "ask_send": "Ask",
    "log": "Log",
    "shares_title": "Agents linked hubs may ask",
    "shares_hint": "Off until you switch an agent on. A linked hub only sees its name and asks it single questions, answered without tools.",
    "share_label": "Share {name} ({profile})",
    "pending_hint": "It asked to link; compare its key with what its owner sees, then approve or refuse.",
    "status": {
      "pending": "Waiting for you",
      "waiting": "Waiting for them",
      "linked": "Linked"
    },
    "direction": {
      "inbound": "It used this hub's invite.",
      "outbound": "This hub used its invite."
    },
    "event": {
      "joined": "It used an invite",
      "requested": "Asked to link",
      "approved": "Approved",
      "approved_by_peer": "They approved",
      "updated": "Changed",
      "unlinked": "Unlinked",
      "unlinked_by_peer": "They unlinked",
      "list_in": "They listed the shared agents",
      "list_out": "Listed their agents",
      "ask_in": "They asked a question",
      "ask_out": "Asked them a question",
      "refused": "Refused a call"
    },
    "reason": {
      "own_url_not_https": "This hub must be reached over HTTPS to link.",
      "https_required": "The link must start with https://.",
      "invite_url_invalid": "That is not an invite link.",
      "invite_refused": "The invite is used, expired or unknown. Ask for a new one.",
      "fingerprint_mismatch": "The other hub answered with a different key than the link names. Not linked.",
      "peer_unreachable": "The other hub did not answer.",
      "peer_exists": "This hub is already linked with it.",
      "peer_is_self": "That is this hub's own invite.",
      "peer_limit": "Too many linked hubs.",
      "invite_limit": "Too many unused invites. Wait for them to expire.",
      "peer_pending": "Their owner has not approved yet.",
      "peer_not_linked": "Not linked yet.",
      "peer_disabled": "The link is switched off.",
      "agent_not_shared": "That agent is not shared.",
      "no_answer": "Their agent gave no answer.",
      "rate_limited": "Too many questions. Try again later.",
      "peer_calls": "Too many calls. Try again in a minute.",
      "peer_asks": "They reached their questions for this hour.",
      "peer_not_pending": "Only a hub waiting for approval can be approved."
    }
  },
  "secrets": {
    "unlock_title": "Enter your password to open Secrets",
    "unlock_hint": "Asked every time this page is opened. It then stays open for five minutes, on this page only.",
    "password": "Your password",
    "unlock": "Open secrets",
    "lock": "Close secrets",
    "expired": "The page closed itself. Enter your password again to open it.",
    "unsupported": "This hub does not offer Secrets yet. Update the hub to use this page.",
    "audit_note": "Values stay hidden. Each one you show or copy is written to the audit log — who, which secret and when, never the value — and hides itself after 30 seconds.",
    "time_left": "Closes in {minutes}:{seconds}",
    "empty": "The hub holds no secrets yet.",
    "shared": "Shared by every profile",
    "whole_hub": "The whole hub",
    "hidden_value": "Hidden value",
    "show": "Show {name}",
    "hide": "Hide {name}",
    "hides_in": "Hides in {seconds}s",
    "copy": "Copy {name}",
    "copied": "Copied",
    "copy_failed": "The browser did not allow copying.",
    "kind": {
      "provider_key": "Provider keys",
      "channel": "Channels",
      "mcp": "MCP servers",
      "webhook_out": "Outgoing webhooks",
      "webhook_in": "Incoming webhooks",
      "other": "Other"
    }
  }
}
